import React, { useState, useEffect } from 'react';
import { useNavigation } from '../navigation';
import DistributorDetails from './Distributor-details';

// API Configuration
const API_CONFIG = {
  BASE_URL: 'https://api.sperow.in',
  USE_NEW_API: true, // Set to false to use only the fallback API
  NEW_API_ENDPOINT: '/pharma/api/search',
  FALLBACK_API_ENDPOINT: '/pharma/api/inventory/search', // Disabled - not used
  USE_FALLBACK_API: false // Disable fallback to inventory search
};

// Utility function to get auth token with debugging
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  console.log('=== Auth Token Debug ===');
  console.log('Token exists:', !!token);
  console.log('Token length:', token ? token.length : 0);
  console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
  return token;
};

// Global function to test purchases API
(window as any).testPurchasesAPI = async () => {
  console.log('=== Testing Purchases API ===');
  
  try {
    const token = getAuthToken();
    console.log('Testing API call...');
    
    const testData = {
      distributor_id: 1,
      items: [{
        medicine_name: "Test Medicine",
        medicine_company: "Test Company",
        medicine_composition: "Test Composition",
        batch_no: "TEST001",
        expiry_date: "2025-12-31",
        quantity: 10,
        unit_cost: 15.00,
        mrp: 30.00,
        tax_percent: 12.0,
        storage_location: "Test Location"
      }]
    };
    
    console.log('Test data:', JSON.stringify(testData, null, 2));
    
    const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(testData)
    });
    
    console.log('✅ API call successful!');
    console.log('Status:', response.status);
    console.log('Headers:', Object.fromEntries(response.headers.entries()));
    
    const data = await response.text();
    console.log('Response:', data);
    
    return { success: true, status: response.status, data };
    
  } catch (error) {
    console.log('❌ API call failed:');
    console.log('Error:', error.message);
    console.log('Type:', error.constructor.name);
    
    return { success: false, error: error.message };
  }
};

// Global function to test with real distributor data
(window as any).testWithRealData = async () => {
  console.log('=== Testing with Real Data ===');
  
  try {
    const token = getAuthToken();
    
    // Get distributors first
    const distributorsResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors/', {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    
    if (distributorsResponse.ok) {
      const distributors = await distributorsResponse.json();
      console.log('Available distributors:', distributors);
      
      if (distributors.length > 0) {
        const testDistributor = distributors[0];
        console.log('Using distributor:', testDistributor);
        
        const testData = {
          distributor_id: testDistributor.id,
          items: [{
            medicine_name: "Paracetamol 500mg",
            medicine_company: "ABC Pharma",
            medicine_composition: "Paracetamol 500mg",
            batch_no: "BATCH001",
            expiry_date: "2025-12-31",
            quantity: 100,
            unit_cost: 15.00,
            mrp: 30.00,
            tax_percent: 12.0,
            storage_location: "Row 5"
          }]
        };
        
        console.log('Test data with real distributor:', JSON.stringify(testData, null, 2));
        
        const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(testData)
        });
        
        console.log('Response status:', response.status);
        const data = await response.text();
        console.log('Response:', data);
        
        return { success: true, status: response.status, data };
      }
    }
    
  } catch (error) {
    console.log('❌ Test failed:', error.message);
    return { success: false, error: error.message };
  }
};

// Global function to test CSV upload API
(window as any).testCSVUpload = async () => {
  console.log('=== Testing CSV Upload API ===');
  
  try {
    const token = getAuthToken();
    
    if (!token) {
      console.log('❌ No auth token found');
      return { success: false, error: 'No auth token' };
    }
    
    // Create a test CSV file
    const csvContent = `medicine_name,medicine_company,medicine_composition,batch_no,expiry_date,quantity,unit_cost,mrp,tax_percent,storage_location
Paracetamol 500mg,ABC Pharma,Paracetamol 500mg,BATCH001,2025-12-31,100,15.00,30.00,12.0,Row 5
Amoxicillin 250mg,XYZ Pharma,Amoxicillin 250mg,BATCH002,2025-11-30,50,25.00,50.00,12.0,Row 3`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const file = new File([blob], 'test-medicines.csv', { type: 'text/csv' });
    
    console.log('Created test file:', file.name, 'Size:', file.size, 'Type:', file.type);
    
    // Test different field names
    const fieldNames = ['file', 'csv_file', 'csv', 'upload', 'medicines_file', 'data'];
    
    for (const fieldName of fieldNames) {
      console.log(`\n--- Testing field name: ${fieldName} ---`);
      
      const formData = new FormData();
      formData.append(fieldName, file);
      formData.append('distributor_id', '1'); // Test with distributor ID 1
      
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      
      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/csv-upload-medicines', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      console.log(`Response status for ${fieldName}:`, response.status);
      
      if (response.ok) {
        const data = await response.text();
        console.log(`✅ Success with field name: ${fieldName}`);
        console.log('Response:', data);
        return { success: true, fieldName, status: response.status, data };
      } else {
        const errorText = await response.text();
        console.log(`❌ Failed with field name: ${fieldName}`);
        console.log('Error:', errorText);
      }
    }
    
    return { success: false, error: 'All field names failed' };
    
  } catch (error) {
    console.log('❌ CSV upload test failed:', error.message);
    return { success: false, error: error.message };
  }
};
import CheckoutBilling from './Checkout-billing';
import Inventory from './Inventory';

interface Distributor {
  id: number;
  name: string;
  contact: string;
  phone: string;
  email: string;
  address: string;
  gstNumber: string;
  settledAmount: number;
  unsettledAmount: number;
  overpaidAmount: number;
}

const MedicationAddition: React.FC = () => {
  const { navigateTo } = useNavigation();

  // Add CSS animations for the data preview
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
        }
        
        /* Hide scrollbar but keep functionality */
        .csv-preview-scroll-container::-webkit-scrollbar {
          display: none;
        }
        
        .csv-preview-scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `;
      document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [activeTab, setActiveTab] = useState<'restock' | 'checkout' | 'inventory'>('restock');
  const [isAnimating, setIsAnimating] = useState(false);
  const [medicationData, setMedicationData] = useState({
    name: '',
    dosage: '',
    quantity: '',
      quantityUnit: 'strip',
    expiryDate: '',
    manufacturer: '',
    batchNumber: '',
    price: '',
    category: 'prescription'
  });
  const [rows, setRows] = useState([
    { id: 1, data: { medicineName: '', composition: '', brandName: '', batchNumber: '', quantity: '', quantityUnit: 'strip', quantityType: 'strip', tabletsPerStrip: '', costPrice: '', mrp: '', gstRate: '', expiryDate: '', purchaseDate: '' } },
    { id: 2, data: { medicineName: '', composition: '', brandName: '', batchNumber: '', quantity: '', quantityUnit: 'strip', quantityType: 'strip', tabletsPerStrip: '', costPrice: '', mrp: '', gstRate: '', expiryDate: '', purchaseDate: '' } },
    { id: 3, data: { medicineName: '', composition: '', brandName: '', batchNumber: '', quantity: '', quantityUnit: 'strip', quantityType: 'strip', tabletsPerStrip: '', costPrice: '', mrp: '', gstRate: '', expiryDate: '', purchaseDate: '' } }
  ]);
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [focusedQuantityRow, setFocusedQuantityRow] = useState<number | null>(null);
  const [focusedMedicineRow, setFocusedMedicineRow] = useState<number | null>(null);
  const [medicineResults, setMedicineResults] = useState<{[key: number]: any[]}>({});
  const [selectedIndex, setSelectedIndex] = useState<{[key: number]: number}>({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const [headerScrollRef, setHeaderScrollRef] = useState<HTMLDivElement | null>(null);
  const [contentScrollRef, setContentScrollRef] = useState<HTMLDivElement | null>(null);
  
  // Responsive flag for mobile (<= 780px)
  const [isMobile, setIsMobile] = useState(false);
  
  // CSV Upload states
  const [isUploadingCSV, setIsUploadingCSV] = useState(false);
  const [uploadedData, setUploadedData] = useState<any>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Permanent CSV data storage (persists after popup closes)
  const [csvUploadedItems, setCsvUploadedItems] = useState<any[]>([]);
  const [csvUploadSummary, setCsvUploadSummary] = useState<any>(null);
  
  // CSV Mapping Modal state
  const [showCsvMappingModal, setShowCsvMappingModal] = useState(false);
  const [tempFieldMapping, setTempFieldMapping] = useState<{[key: string]: string}>({});
  const [csvPreviewMode, setCsvPreviewMode] = useState<'columns' | 'preview'>('columns');
  const [isTablet, setIsTablet] = useState(false);

  // Pending purchases state
  const [pendingPurchases, setPendingPurchases] = useState<any[]>([]);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [showPendingPurchases, setShowPendingPurchases] = useState(false);
  const [purchasesError, setPurchasesError] = useState<string | null>(null);
  
  // Edit functionality state
  const [editingPurchase, setEditingPurchase] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdatingPurchase, setIsUpdatingPurchase] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  
  // Confirm functionality state
  const [confirmingPurchaseId, setConfirmingPurchaseId] = useState<string | null>(null);
  
  // Drag and drop functionality state
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [fieldMapping, setFieldMapping] = useState<{[key: string]: string}>({});
  const [isDragging, setIsDragging] = useState(false);
  const [dynamicFields, setDynamicFields] = useState<string[]>([]);
  const [touchStartPos, setTouchStartPos] = useState<{x: number, y: number} | null>(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingFieldName, setEditingFieldName] = useState<string>('');
  
  const [csvHeaderScrollRef, setCsvHeaderScrollRef] = useState<HTMLDivElement | null>(null);
  const [csvBodyScrollRef, setCsvBodyScrollRef] = useState<HTMLDivElement | null>(null);

  // CSV table scroll handlers
  const handleCsvHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    console.log('Header scroll:', scrollLeft);
    if (csvBodyScrollRef && csvBodyScrollRef.scrollLeft !== scrollLeft) {
      console.log('Syncing body to header scroll:', scrollLeft);
      csvBodyScrollRef.scrollLeft = scrollLeft;
    }
  };

  const handleCsvBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    console.log('Body scroll:', scrollLeft);
    if (csvHeaderScrollRef && csvHeaderScrollRef.scrollLeft !== scrollLeft) {
      console.log('Syncing header to body scroll:', scrollLeft);
      csvHeaderScrollRef.scrollLeft = scrollLeft;
    }
  };

  // Handle scroll position tracking and synchronization
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrollLeft = element.scrollLeft;
    const scrollWidth = element.scrollWidth;
    const clientWidth = element.clientWidth;
    
    setScrollPosition(scrollLeft);
    setScrollWidth(scrollWidth);
    setClientWidth(clientWidth);
    
    // Synchronize header scroll with content scroll
    if (element === contentScrollRef && headerScrollRef) {
      headerScrollRef.scrollLeft = scrollLeft;
    }
  };

  // Handle header scroll synchronization
  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrollLeft = element.scrollLeft;
    
    // Synchronize content scroll with header scroll
    if (element === headerScrollRef && contentScrollRef) {
      contentScrollRef.scrollLeft = scrollLeft;
    }
  };

  // Calculate scrollbar indicator position and width
  const getScrollbarStyle = () => {
    if (scrollWidth <= clientWidth) {
      return { display: 'none' as const };
    }
    
    const scrollbarWidth = (clientWidth / scrollWidth) * clientWidth;
    const scrollbarPosition = (scrollPosition / (scrollWidth - clientWidth)) * (clientWidth - scrollbarWidth);
    
    return {
      width: `${scrollbarWidth}px`,
      left: `${scrollbarPosition}px`,
      height: '4px',
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      borderRadius: '2px',
      position: 'absolute' as const,
      bottom: '2px',
      zIndex: 2,
      transition: 'all 0.1s ease',
      pointerEvents: 'none' as const
    };
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (focusedQuantityRow !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-quantity-dropdown]')) {
          setFocusedQuantityRow(null);
        }
      }
      if (focusedMedicineRow !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-medicine-dropdown]')) {
          setFocusedMedicineRow(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [focusedQuantityRow, focusedMedicineRow]);

  // Auto-scroll selected item into view in medicine dropdown
  useEffect(() => {
    if (focusedMedicineRow !== null && medicineResults[focusedMedicineRow]?.length > 0) {
      const dropdownContainer = document.querySelector(`[data-dropdown-container="${focusedMedicineRow}"]`);
      const selectedItem = document.querySelector(`[data-dropdown-item][data-dropdown-index="${selectedIndex[focusedMedicineRow] || 0}"]`);
      
      if (dropdownContainer && selectedItem) {
        selectedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [selectedIndex, focusedMedicineRow, medicineResults]);

  // Watch for viewport width to toggle mobile layout
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
      setIsTablet(width <= 768 && width > 480);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Initialize scroll dimensions
  useEffect(() => {
    const tableContainer = document.querySelector('.table-container') as HTMLDivElement;
    if (tableContainer) {
      const scrollWidth = tableContainer.scrollWidth;
      const clientWidth = tableContainer.clientWidth;
      setScrollWidth(scrollWidth);
      setClientWidth(clientWidth);
    }
  }, [rows]); // Recalculate when rows change

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setMedicationData({
      ...medicationData,
      [e.target.name]: e.target.value
    });
  };

  // Reset selectedIndex when dropdown opens
  React.useEffect(() => {
    if (focusedMedicineRow !== null) {
      setSelectedIndex(prev => ({ ...prev, [focusedMedicineRow]: 0 }));
    }
  }, [focusedMedicineRow]);

  const handleRowInputChange = (rowId: number, field: string, value: string) => {
    setRows(prevRows => 
      prevRows.map(row => 
      row.id === rowId 
        ? { ...row, data: { ...row.data, [field]: value } }
        : row
      )
    );
  };

  // Medicine search function
  const searchMedicines = async (query: string, rowId: number) => {
    console.log('🔍 searchMedicines called with:', { query, rowId });
    
    if (!query.trim()) {
      console.log('❌ Empty query, clearing results');
      setMedicineResults(prev => ({ ...prev, [rowId]: [] }));
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.error('❌ No auth token found');
      return;
    }

      try {
      console.log(`🔍 Searching medicines for: "${query}"`);
        
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/search?q=${encodeURIComponent(query)}&limit=10`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📡 API Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Medicine search successful:', data);
        
        // Extract the medicines array from the response
        // Handle different possible response structures
        let medicines: any[] = [];
        if (Array.isArray(data)) {
          medicines = data;
        } else if (data && data.inventory && Array.isArray(data.inventory)) {
          medicines = data.inventory; // New API structure
        } else if (data && data.medicines && Array.isArray(data.medicines)) {
          medicines = data.medicines;
        } else if (data && data.results && Array.isArray(data.results)) {
          medicines = data.results;
        } else if (data && data.data && Array.isArray(data.data)) {
          medicines = data.data;
        } else if (data && data.items && Array.isArray(data.items)) {
          medicines = data.items;
        } else {
          console.warn('⚠️ Unexpected response structure:', data);
          medicines = [];
        }
        console.log('📋 Extracted medicines:', medicines);
        
        // Log the first medicine structure to understand field names
        if (medicines.length > 0) {
          console.log('🔍 First medicine structure:', medicines[0]);
          console.log('🔍 Available fields:', Object.keys(medicines[0]));
          console.log('🔍 Raw medicine object:', JSON.stringify(medicines[0], null, 2));
        }
        
        setMedicineResults(prev => ({ 
          ...prev, 
          [rowId]: medicines // API already limits to 10 results
        }));
      } else {
        console.error('❌ Medicine search failed:', response.status, response.statusText);
        setMedicineResults(prev => ({ ...prev, [rowId]: [] }));
      }
    } catch (error) {
      console.error('❌ Medicine search error:', error);
      setMedicineResults(prev => ({ ...prev, [rowId]: [] }));
    }
  };

  const addNewRow = () => {
    const newId = Math.max(...rows.map(row => row.id)) + 1;
    setRows([...rows, { 
      id: newId, 
      data: { 
        medicineName: '', 
        composition: '', 
        brandName: '', 
        batchNumber: '', 
        quantity: '', 
        quantityUnit: 'strip', 
        quantityType: 'strip', 
        tabletsPerStrip: '', 
        costPrice: '', 
        mrp: '', 
        gstRate: '', 
        expiryDate: '', 
        purchaseDate: ''
      } 
    }]);
  };

  const removeRow = (rowId: number) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== rowId));
    } else {
      alert('Cannot remove the last row. At least one row is required.');
    }
  };

  // Function to open edit modal
  const openEditModal = (purchase: any) => {
    setEditingPurchase(purchase);
    setEditFormData({
      distributor_id: purchase.distributor_id || null,
      distributor_name: purchase.distributor_name || '',
      payment_mode: purchase.payment_mode || 'credit',
      payment_details: purchase.payment_details || '',
      items: purchase.items.map((item: any) => ({
        medicine_name: item.medicine_name || '',
        medicine_company: item.medicine_company || item.supplier_name || '',
        batch_no: item.batch_no || item.batch_number || '',
        expiry_date: item.expiry_date || '',
        quantity: parseInt(item.quantity) || 0,
        cost_price_per_unit: parseFloat(item.cost_price_per_unit || item.purchase_price_per_unit) || 0,
        mrp: parseFloat(item.mrp) || 0,
        tax_amount: parseFloat(item.tax_amount) || 0,
        storage_location: item.storage_location || '',
        quantity_type: item.quantity_type || 'strip',
        tablets_per_strip: parseInt(item.tablets_per_strip) || 0,
        total_tablets: parseInt(item.total_tablets) || 0
      }))
    });
    setShowEditModal(true);
  };

  // Function to close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPurchase(null);
    setEditFormData({});
  };
  // Function to update pending purchase
  const updatePendingPurchase = async () => {
    if (!editingPurchase || !editingPurchase.pending_id) {
      alert('No purchase selected for editing');
      return;
    }

    try {
      setIsUpdatingPurchase(true);
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      console.log('Updating pending purchase:', editingPurchase.pending_id);
      console.log('Update data:', editFormData);
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/pending/${editingPurchase.pending_id}/edit`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(editFormData)
      });

      console.log('Update response:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to update purchase (Status: ${response.status}): ${errorText}`);
      }

      const updatedData = await response.json();
      console.log('Updated purchase data:', updatedData);
      
      // Update the local state with the updated purchase
      setPendingPurchases(prevPurchases => 
        prevPurchases.map(purchase => 
          purchase.pending_id === editingPurchase.pending_id 
            ? { ...purchase, ...editFormData }
            : purchase
        )
      );
      
      alert('Purchase updated successfully!');
      closeEditModal();
      
    } catch (error) {
      console.error('Error updating pending purchase:', error);
      alert(`Failed to update purchase: ${error.message}`);
    } finally {
      setIsUpdatingPurchase(false);
    }
  };

  // Function to handle item field changes in edit form
  const handleItemFieldChange = (itemIndex: number, field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      items: prev.items.map((item: any, index: number) => 
        index === itemIndex 
          ? { ...item, [field]: value }
          : item
      )
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, fieldName: string) => {
    setDraggedField(fieldName);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldName);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    setIsDragging(false);
  };

  // Touch event handlers for mobile drag and drop
  const handleTouchStart = (e: React.TouchEvent, fieldName: string) => {
    if (isMobile) {
      const touch = e.touches[0];
      setTouchStartPos({ x: touch.clientX, y: touch.clientY });
      setDraggedField(fieldName);
      setIsTouchDragging(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isMobile && touchStartPos && draggedField) {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.y);
      
      // Start dragging if moved more than 10px
      if (deltaX > 10 || deltaY > 10) {
        setIsTouchDragging(true);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isMobile && draggedField) {
      const touch = e.changedTouches[0];
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (elementBelow) {
        const dropTarget = elementBelow.closest('[data-drop-target]');
        if (dropTarget) {
          const targetField = dropTarget.getAttribute('data-drop-target');
          if (targetField) {
            setTempFieldMapping(prev => ({ ...prev, [targetField]: draggedField }));
          }
        }
      }
      
      setDraggedField(null);
      setTouchStartPos(null);
      setIsTouchDragging(false);
    }
  };

  const handleFieldDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFieldDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    const draggedFieldName = e.dataTransfer.getData('text/plain');
    
    if (draggedFieldName && targetField) {
      // Update existing field mapping
      setFieldMapping(prev => ({
        ...prev,
        [targetField]: draggedFieldName
      }));
      autoFillColumn(targetField, draggedFieldName);
    }
    
    setDraggedField(null);
    setIsDragging(false);
  };

  const clearFieldMapping = (targetField: string) => {
    setFieldMapping(prev => {
      const newMapping = { ...prev };
      delete newMapping[targetField];
      return newMapping;
    });
  };

  const clearAllFieldMappings = () => {
    const hasMappings = Object.keys(fieldMapping).length > 0 || Object.keys(tempFieldMapping).length > 0;
    
    if (hasMappings) {
      const confirmed = window.confirm(
        'Are you sure you want to clear all field mappings? This action cannot be undone.'
      );
      
      if (confirmed) {
        setFieldMapping({});
        setTempFieldMapping({});
      }
    }
  };
  // Auto-fill column with CSV data
  const autoFillColumn = (targetField: string, csvField: string) => {
    if (!csvUploadedItems || csvUploadedItems.length === 0) return;
    
    console.log('Auto-filling column:', targetField, 'with CSV field:', csvField);
    console.log('CSV data sample:', csvUploadedItems[0]);
    console.log('Total CSV items:', csvUploadedItems.length);
    
    // Update existing rows with new data, preserving existing data
    setRows(prevRows => {
      const updatedRows = prevRows.map((row, index) => {
        const csvValue = csvUploadedItems[index]?.[csvField] || csvUploadedItems[0]?.[csvField] || '';
        console.log(`Row ${index}: Setting ${targetField} = ${csvValue}`);
        
        return {
          ...row,
          data: {
            ...row.data,
            [targetField]: csvValue
          }
        };
      });
      
      // If we have more CSV items than rows, add new rows
      if (csvUploadedItems.length > prevRows.length) {
        const additionalRows = csvUploadedItems.slice(prevRows.length).map((csvItem, index) => {
          const newId = Math.max(...prevRows.map(r => r.id)) + index + 1;
          return {
            id: newId,
            data: {
              medicineName: '',
              composition: '',
              brandName: '',
              batchNumber: '',
              quantity: '',
              quantityUnit: 'strip',
              quantityType: 'strip',
              tabletsPerStrip: '', 
              costPrice: '',
              mrp: '',
              gstRate: '',
              expiryDate: '',
              purchaseDate: '',
              [targetField]: csvItem[csvField] || ''
            }
          };
        });
        return [...updatedRows, ...additionalRows];
      }
      
      return updatedRows;
    });
  };

  // Function to start editing a field
  const startEditingField = (fieldKey: string) => {
    console.log('✏️ Starting to edit field:', fieldKey);
    setEditingField(fieldKey);
    setEditingFieldName(fieldMapping[fieldKey] || '');
  };

  // Function to save field name
  const saveFieldName = (fieldKey: string) => {
    console.log('💾 Saving field name:', editingFieldName);
    if (editingFieldName.trim()) {
      setFieldMapping(prev => ({
        ...prev,
        [fieldKey]: editingFieldName.trim()
      }));
      setEditingField(null);
      setEditingFieldName('');
      console.log('✅ Field name saved successfully');
    } else {
      console.log('❌ Empty field name, not saving');
    }
  };

  // Function to cancel editing
  const cancelEditing = () => {
    console.log('❌ Canceling field edit');
    setEditingField(null);
    setEditingFieldName('');
  };

  // Function to confirm pending purchase
  const confirmPendingPurchase = async (purchase: any) => {
    if (!purchase.pending_id) {
      alert('No purchase ID found for confirmation');
      return;
    }

    try {
      setConfirmingPurchaseId(purchase.pending_id);
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      console.log('Confirming pending purchase:', purchase.pending_id);
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/pending/${purchase.pending_id}/confirm`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          pending_id: purchase.pending_id,
          pending_purchase_id: purchase.pending_id
        })
      });

      console.log('Confirm response:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to confirm purchase (Status: ${response.status}): ${errorText}`);
      }

      const confirmedData = await response.json();
      console.log('Confirmed purchase data:', confirmedData);
      
      // Update the local state - remove the confirmed purchase from pending list
      setPendingPurchases(prevPurchases => 
        prevPurchases.filter(p => p.pending_id !== purchase.pending_id)
      );
      
      alert('Purchase confirmed successfully!');
      
    } catch (error) {
      console.error('Error confirming pending purchase:', error);
      alert(`Failed to confirm purchase: ${error.message}`);
    } finally {
      setConfirmingPurchaseId(null);
    }
  };

  // Function to fetch pending purchases
  const fetchPendingPurchases = async () => {
    try {
      setIsLoadingPurchases(true);
      setPurchasesError(null);
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      console.log('Fetching pending purchases...');
      
      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/pending', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });

      console.log('Pending purchases response:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch purchases (Status: ${response.status}): ${errorText}`);
      }

      const data = await response.json();
      console.log('Pending purchases data:', data);
      
      // Handle the specific API response format with pending_purchases
      const purchases = data.pending_purchases || data.purchases || data.data || data || [];
      setPendingPurchases(Array.isArray(purchases) ? purchases : []);
      setShowPendingPurchases(true);
      
    } catch (error) {
      console.error('Error fetching pending purchases:', error);
      setPurchasesError(error.message);
      alert(`Failed to fetch pending purchases: ${error.message}`);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'text/csv') {
      alert('Please select a valid CSV file.');
      return;
    }

    try {
      setIsUploadingCSV(true);
      setUploadError(null);
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      console.log('Uploading CSV file:', file.name);
      console.log('File size:', file.size);
      console.log('File type:', file.type);
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Try the most common field name first
      formData.append('file', file);
      
      // Add distributor_id if selected
      if (selectedDistributor && selectedDistributor.id > 0) {
        formData.append('distributor_id', selectedDistributor.id.toString());
        console.log('Added distributor_id:', selectedDistributor.id);
      }

      // Debug FormData contents
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/csv-upload-medicines', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
          // Note: Don't set Content-Type header for FormData - let browser set it with boundary
        },
        body: formData
      });

      console.log('CSV upload response:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Upload failed (Status: ${response.status}): ${errorText}`);
      }

      const result = await response.json();
      console.log('CSV upload successful:', result);
      
      // Store uploaded data (no popup needed)
      setUploadedData(result);
      
      // Store data permanently for main component display
      const items = result.medicines || result.purchase?.items || [];
      console.log('CSV items structure:', items);
      console.log('First item fields:', items[0] ? Object.keys(items[0]) : 'No items');
      
      setCsvUploadedItems(items);
      setCsvUploadSummary({
        items_count: result.items_count,
        message: result.message,
        pending_id: result.pending_id,
        upload_time: new Date().toLocaleString()
      });
      
      // Open mapping modal after upload if there is data
      if (items && items.length > 0) {
        // initialize temp mapping with existing mapping (if any)
        setTempFieldMapping(prev => ({ ...fieldMapping }));
        setShowCsvMappingModal(true);
      }
      
      // Clear the file input
      event.target.value = '';
      
    } catch (error) {
      console.error('CSV upload error:', error);
      setUploadError(error.message);
      alert(`Failed to upload CSV: ${error.message}`);
    } finally {
      setIsUploadingCSV(false);
    }
  };

  const parseCSVAndUpdateRows = (csvContent: string) => {
    try {
      const lines = csvContent.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        alert('CSV file must contain at least a header row and one data row.');
        return;
      }
      
      const newRows: Array<{id: number, data: {medicineName: string, composition: string, brandName: string, batchNumber: string, quantity: string, quantityUnit: string, quantityType: string, tabletsPerStrip: string, costPrice: string, mrp: string, gstRate: string, expiryDate: string, purchaseDate: string}}> = [];
      
      // Skip header row if it exists
      const dataLines = lines.slice(1);
      
      dataLines.forEach((line, index) => {
        const columns = line.split(',').map(col => col.trim().replace(/"/g, ''));
        if (columns.length >= 11) {
          newRows.push({
            id: index + 1,
            data: {
              medicineName: columns[0] || '',
              composition: columns[1] || '',
              brandName: columns[2] || '',
              batchNumber: columns[3] || '',
              quantity: columns[4] || '',
              quantityUnit: 'strip', // Default to strip for CSV imports
              quantityType: 'strip', // Default to strip for CSV imports
              tabletsPerStrip: '',
              costPrice: columns[5] || '',
              mrp: columns[6] || '',
              gstRate: columns[7] || '',
              expiryDate: columns[8] || '',
              purchaseDate: ''
            }
          });
        }
      });
      
      if (newRows.length > 0) {
        setRows(newRows);
        alert(`Successfully imported ${newRows.length} rows from CSV file!`);
      } else {
        alert('No valid data found in the CSV file. Please check the format.');
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert('Error parsing CSV file. Please check the file format.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv') {
        const reader = new FileReader();
        reader.onload = (event) => {
          const csv = event.target?.result as string;
          parseCSVAndUpdateRows(csv);
        };
        reader.readAsText(file);
      } else {
        alert('Please drop a valid CSV file.');
      }
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== SUBMIT BUTTON CLICKED ===');
    console.log('📊 TABLE DATA SUBMITTED:');
    console.log('Total rows in table:', rows.length);
    console.log('All rows data:', rows);
    console.log('Selected distributor:', selectedDistributor);
    console.log('================================');
    
    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        alert('Authentication token not found. Please log in again.');
        return;
      }

      // Filter out empty rows and prepare data for API
      const validRows = rows.filter(row => 
        row.data.medicineName.trim() !== '' && 
        row.data.brandName.trim() !== '' && 
        row.data.composition.trim() !== '' &&
        row.data.batchNumber.trim() !== '' &&
        row.data.quantity.trim() !== '' &&
        row.data.costPrice.trim() !== '' &&
        row.data.mrp.trim() !== '' &&
        row.data.expiryDate.trim() !== ''
      );

      console.log('=== VALIDATION CHECKS ===');
      console.log('📋 VALIDATION RESULTS:');
      console.log('Total rows:', rows.length);
      console.log('Valid rows:', validRows.length);
      console.log('Valid rows data:', validRows);
      console.log('Selected distributor:', selectedDistributor);
      console.log('Auth token exists:', !!authToken);
      console.log('========================');

      if (validRows.length === 0) {
        console.log('❌ VALIDATION FAILED: No valid rows found');
        alert('Please fill in at least one complete row with all required fields.');
        return;
      }

      if (!selectedDistributor) {
        console.log('❌ VALIDATION FAILED: No distributor selected');
        alert('Please select a distributor before submitting.');
        return;
      }

      // Additional distributor validation
      if (!selectedDistributor.id || !selectedDistributor.name) {
        console.log('❌ VALIDATION FAILED: Invalid distributor data');
        console.log('Distributor data:', selectedDistributor);
        alert('Invalid distributor data. Please select a valid distributor.');
        return;
      }

      // Check if distributor ID is valid (not 0 or negative)
      if (selectedDistributor.id <= 0) {
        console.log('❌ VALIDATION FAILED: Invalid distributor ID');
        console.log('Distributor ID:', selectedDistributor.id);
        alert('Please select a distributor from the list instead of manually entering the name. The distributor ID must be valid.');
        return;
      }

      console.log('✅ VALIDATION PASSED: Proceeding with API call');

      // Prepare medicines data according to API format
      console.log('=== PREPARING MEDICINE DATA ===');
      console.log('🔄 PROCESSING VALID ROWS:');
      const medicinesData = validRows.map((row, index) => {
        const medicineData = {
          medicine_name: row.data.medicineName.trim(),
          medicine_company: row.data.brandName.trim(),
          medicine_composition: row.data.composition.trim(),
          batch_no: row.data.batchNumber.trim(),
          expiry_date: row.data.expiryDate.trim(),
          quantity: parseInt(row.data.quantity) || 0,
          unit_cost: parseFloat(row.data.costPrice) || 0.00,
          mrp: parseFloat(row.data.mrp) || 0.00,
          tax_percent: parseFloat(row.data.gstRate) || 0.0
        };
        
        // Log each medicine data for debugging
        console.log(`📦 Medicine ${index + 1}:`, {
          name: medicineData.medicine_name,
          company: medicineData.medicine_company,
          composition: medicineData.medicine_composition,
          batch: medicineData.batch_no,
          expiry: medicineData.expiry_date,
          quantity: medicineData.quantity,
          cost: medicineData.unit_cost,
          mrp: medicineData.mrp,
          tax: medicineData.tax_percent
        });
        
        // Validate medicine data
        if (!medicineData.medicine_name || !medicineData.medicine_company || 
            !medicineData.batch_no || !medicineData.expiry_date || 
            medicineData.quantity <= 0 || medicineData.unit_cost <= 0 || medicineData.mrp <= 0) {
          console.error('❌ Invalid medicine data:', medicineData);
          throw new Error(`Invalid medicine data for: ${medicineData.medicine_name || 'Unknown'}`);
        }
        
        // Check date format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(medicineData.expiry_date)) {
          console.warn('⚠️ Date format might be invalid:', medicineData.expiry_date);
          console.warn('Expected format: YYYY-MM-DD (e.g., 2025-12-31)');
        }
        
        return medicineData;
      });

      // Prepare request body according to API specification
      const requestBody = {
        distributor_id: selectedDistributor?.id || null,
        items: medicinesData
      };

      console.log('=== FINAL REQUEST BODY ===');
      console.log('📤 API REQUEST DATA:');
      console.log('Request body:', JSON.stringify(requestBody, null, 2));
      console.log('Distributor details:', {
        id: selectedDistributor?.id,
        name: selectedDistributor?.name,
        email: selectedDistributor?.email,
        phone: selectedDistributor?.phone
      });
      
      // Additional debugging for API format
      console.log('=== API FORMAT VALIDATION ===');
      console.log('Items count:', medicinesData.length);
      console.log('Each item structure:', medicinesData.map((med, index) => ({
        index,
        has_name: !!med.medicine_name,
        has_company: !!med.medicine_company,
        has_composition: !!med.medicine_composition,
        has_batch: !!med.batch_no,
        has_expiry: !!med.expiry_date,
        quantity_valid: med.quantity > 0,
        cost_valid: med.unit_cost > 0,
        mrp_valid: med.mrp > 0,
        tax_valid: med.tax_percent >= 0
      })));
      console.log('Distributor ID valid:', selectedDistributor?.id > 0);
      console.log('Request body format:', {
        has_distributor_id: !!requestBody.distributor_id,
        has_items: Array.isArray(requestBody.items),
        items_count: requestBody.items?.length || 0
      });
      console.log('=============================');

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody),
      });

      console.log('API response received:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const result = await response.json();
        console.log('Medicines added successfully:', result);
        alert(`Successfully added ${validRows.length} medicine(s)!`);
        
        // Reset form data
        setRows([
          { id: 1, data: { medicineName: '', composition: '', brandName: '', batchNumber: '', quantity: '', quantityUnit: 'strip', quantityType: 'strip', tabletsPerStrip: '', costPrice: '', mrp: '', gstRate: '', expiryDate: '', purchaseDate: '' } },
          { id: 2, data: { medicineName: '', composition: '', brandName: '', batchNumber: '', quantity: '', quantityUnit: 'strip', quantityType: 'strip', tabletsPerStrip: '', costPrice: '', mrp: '', gstRate: '', expiryDate: '', purchaseDate: '' } },
          { id: 3, data: { medicineName: '', composition: '', brandName: '', batchNumber: '', quantity: '', quantityUnit: 'strip', quantityType: 'strip', tabletsPerStrip: '', costPrice: '', mrp: '', gstRate: '', expiryDate: '', purchaseDate: '' } }
        ]);
        setSelectedDistributor(null);
      } else {
        const errorText = await response.text();
        console.error('Failed to add medicines:', response.status, errorText);
        
        // Try to parse error as JSON for better error handling
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch (e) {
          // If not JSON, use the text as is
        }
        
        alert(`Failed to add medicines (Status: ${response.status}): ${errorMessage}`);
        
        // Log detailed request information for debugging
        console.log('=== DEBUGGING INFO ===');
        console.log('Request URL:', 'https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/');
        console.log('Request Method:', 'POST');
        console.log('Request Headers:', {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken ? 'Present' : 'Missing'}`
        });
        console.log('Request Body:', requestBody);
        console.log('Selected Distributor:', selectedDistributor);
        console.log('Valid Rows Count:', validRows.length);
        console.log('====================');
      }
    } catch (error) {
      console.error('Network error:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      
      // Enhanced error handling for CORS and network issues
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        alert(`
          CORS Error: Unable to connect to the medications API.
          
          This error occurs because the server doesn't allow requests from localhost:3000.
          
          Possible solutions:
          1. Server needs CORS headers: Access-Control-Allow-Origin: https://localhost:3000
          2. Check if the server is running and accessible
          3. Verify the API endpoint URL is correct
          4. Contact the server administrator to fix CORS configuration
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/
          
          You can test the API manually by running: testPurchasesAPI()
        `);
      } else if (error.message.includes('ERR_CERT_COMMON_NAME_INVALID') || 
                 error.message.includes('ERR_CERT_AUTHORITY_INVALID') ||
                 error.message.includes('ERR_SSL_PROTOCOL_ERROR') ||
                 error.message.includes('certificate')) {
        alert('SSL Certificate Error: The server certificate is invalid. Please contact the administrator or try using HTTP instead of HTTPS.');
      } else {
        alert(`Network error: ${error.message}. Please check your connection and try again.`);
      }
    }
  };

  // Animation helper functions
  const getTabPosition = () => {
    switch (activeTab) {
      case 'restock':
        return '2px';
      case 'checkout':
        return 'calc(33.33% + 1px)';
      case 'inventory':
        return 'calc(66.66% + 1px)';
      default:
        return '2px';
    }
  };

  const getButtonColor = (tab: 'restock' | 'checkout' | 'inventory') => {
    return activeTab === tab ? 'white' : '#64748b';
  };

  const triggerAnimation = (targetTab: 'restock' | 'checkout' | 'inventory') => {
    console.log('Triggering animation to:', targetTab, 'from:', activeTab);
    setIsAnimating(true);
    setActiveTab(targetTab);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 700);
  };
  return (
    <>
      <style>
        {`
          /* Custom Horizontal Scrollbar Design */
          .table-scrollable-content {
            overflow-x: auto;
            overflow-y: auto;
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
            min-width: 1400px; /* Ensure minimum width for proper scrolling */
          }
          
          .table-scrollable-content::-webkit-scrollbar {
            width: 0px;
            height: 0px;
            background: transparent;
          }
          
          .table-scrollable-content::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .table-scrollable-content::-webkit-scrollbar-thumb {
            background: transparent;
          }

          /* Header Scrollable Area */
          .table-header-scrollable {
            overflow-x: auto;
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          .table-header-scrollable::-webkit-scrollbar {
            width: 0px;
            height: 0px;
            background: transparent;
          }
          
          .table-header-scrollable::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .table-header-scrollable::-webkit-scrollbar-thumb {
            background: transparent;
          }
          
          
          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            /* Main Container */
            .main-container {
              padding: 0.5rem !important;
              margin: 0 !important;
            }
            
            /* Header Section */
            .header-section {
              padding: 0.75rem !important;
              margin-bottom: 0.75rem !important;
            }
            
            .header-container {
              flex-direction: column !important;
              gap: 1rem !important;
              padding: 0 !important;
              align-items: stretch !important;
            }
            
            .back-button {
              align-self: flex-start !important;
              order: 1 !important;
              width: 40px !important;
              height: 40px !important;
              font-size: 1.2rem !important;
            }
            
            .header-title {
              order: 2 !important;
              position: static !important;
              transform: none !important;
              font-size: 1.2rem !important;
              text-align: center !important;
              margin: 0 !important;
              padding: 0.5rem 0 !important;
            }
            
            .tab-switcher {
              order: 3 !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              margin-top: 0.5rem !important;
            }
            
            .tab-button {
              font-size: 0.8rem !important;
              padding: 0.6rem 0.5rem !important;
              flex: 1 !important;
          }
          
            /* Table Container */
            .table-container {
              margin: 0 0.25rem 1rem 0.25rem !important;
              width: calc(100% - 0.5rem) !important;
              border-radius: 12px !important;
              overflow: hidden !important;
            }
            
            .table-scrollable-content {
              overflow-x: auto !important;
              min-width: 1200px !important;
              -webkit-overflow-scrolling: touch !important;
            }
            
            .table-header {
              grid-template-columns: 50px 180px 120px 100px 100px 80px 80px 90px 90px 70px 100px 70px !important;
              gap: 0.5rem !important;
              padding: 0.75rem !important;
              font-size: 0.7rem !important;
              min-width: 1200px !important;
            }
            
            .table-row {
              grid-template-columns: 50px 180px 120px 100px 100px 80px 80px 90px 90px 70px 100px 70px !important;
              gap: 0.5rem !important;
              padding: 0.75rem !important;
              min-width: 1200px !important;
            }
            
            .table-input {
              padding: 0.4rem !important;
              font-size: 0.7rem !important;
              border-radius: 4px !important;
              min-height: 32px !important;
            }
            
            /* Action Buttons */
            .action-buttons {
              padding: 1rem !important;
              flex-direction: column !important;
              gap: 0.75rem !important;
              margin: 0 0.25rem !important;
            }
            
            .action-button {
              padding: 0.75rem 1rem !important;
              font-size: 0.85rem !important;
              width: 100% !important;
              min-width: unset !important;
              border-radius: 8px !important;
            }
            
            .submit-button {
              padding: 0.75rem 1.5rem !important;
              font-size: 0.9rem !important;
              border-radius: 10px !important;
              width: 100% !important;
            }
            
            /* CSV Upload Section */
            .csv-upload-section {
              margin: 0 0.25rem 1rem 0.25rem !important;
              padding: 1rem !important;
              border-radius: 12px !important;
            }
            
            .csv-upload-button {
              width: auto !important;
              padding: 0.75rem !important;
              font-size: 0.85rem !important;
            }
            
            /* Pending Purchases Section */
            .pending-purchases-section {
              margin: 0 0.25rem !important;
              padding: 1rem !important;
              border-radius: 12px !important;
            }
            
            .pending-purchase-card {
              padding: 1rem !important;
              margin-bottom: 0.75rem !important;
              border-radius: 8px !important;
            }
            
            .pending-purchase-header {
              flex-direction: column !important;
              gap: 0.5rem !important;
              align-items: flex-start !important;
            }
            
            .pending-purchase-actions {
              flex-direction: column !important;
              gap: 0.5rem !important;
              width: 100% !important;
            }
            
            .pending-purchase-button {
              width: 100% !important;
              padding: 0.6rem !important;
              font-size: 0.8rem !important;
            }
          }
          
          @media (max-width: 480px) {
            /* Extra Small Mobile Styles */
            .main-container {
              padding: 0.25rem !important;
            }
            
            .header-section {
              padding: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            
            .back-button {
              width: 36px !important;
              height: 36px !important;
              font-size: 1rem !important;
            }
            
            .header-title {
              font-size: 1rem !important;
              padding: 0.25rem 0 !important;
            }
            
            .tab-switcher {
              margin-top: 0.25rem !important;
            }
            
            .tab-button {
              font-size: 0.7rem !important;
              padding: 0.5rem 0.25rem !important;
            }
            
            /* Table Container */
            .table-container {
              margin: 0 0.125rem 0.75rem 0.125rem !important;
              width: calc(100% - 0.25rem) !important;
              border-radius: 8px !important;
            }
            
            .table-scrollable-content {
              min-width: 1000px !important;
            }
            
            .table-header {
              grid-template-columns: 40px 160px 100px 80px 80px 70px 70px 80px 80px 60px 90px 60px !important;
              font-size: 0.65rem !important;
              padding: 0.5rem !important;
              min-width: 1000px !important;
              gap: 0.25rem !important;
            }
            
            .table-row {
              grid-template-columns: 40px 160px 100px 80px 80px 70px 70px 80px 80px 60px 90px 60px !important;
              padding: 0.5rem !important;
              min-width: 1000px !important;
              gap: 0.25rem !important;
            }
            
            .table-input {
              padding: 0.3rem !important;
              font-size: 0.65rem !important;
              border-radius: 3px !important;
              min-height: 28px !important;
            }
            
            /* Action Buttons */
            .action-buttons {
              padding: 0.75rem !important;
              gap: 0.5rem !important;
              margin: 0 0.125rem !important;
            }
            
            .action-button {
              padding: 0.6rem 0.75rem !important;
              font-size: 0.8rem !important;
            }
            
            .submit-button {
              padding: 0.6rem 1rem !important;
              font-size: 0.85rem !important;
              border-radius: 8px !important;
            }
            
            /* CSV Upload Section */
            .csv-upload-section {
              margin: 0 0.125rem 0.75rem 0.125rem !important;
              padding: 0.75rem !important;
              border-radius: 8px !important;
            }
            
            .csv-upload-button {
              padding: 0.6rem !important;
              font-size: 0.8rem !important;
            }
            
            /* Pending Purchases Section */
            .pending-purchases-section {
              margin: 0 0.125rem !important;
              padding: 0.75rem !important;
              border-radius: 8px !important;
            }
            
            .pending-purchase-card {
              padding: 0.75rem !important;
              margin-bottom: 0.5rem !important;
              border-radius: 6px !important;
            }
            
            .pending-purchase-button {
              padding: 0.5rem !important;
              font-size: 0.75rem !important;
            }
          }

          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* CSV Table Scrolling Styles */
          .csv-table-body {
            scrollbar-width: thin;
            scrollbar-color: #10b981 rgba(16, 185, 129, 0.1);
          }
          
          .csv-table-body::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          .csv-table-body::-webkit-scrollbar-track {
            background: rgba(16, 185, 129, 0.1);
            border-radius: 4px;
          }
          
          .csv-table-body::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border-radius: 4px;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          
          .csv-table-body::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
          }
          
          .csv-table-body::-webkit-scrollbar-corner {
            background: rgba(16, 185, 129, 0.1);
          }
        `}
      </style>
    <div className="main-container" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overflowX: 'hidden'
    }}>
      {/* Top Header */}
      <div className="header-section" style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderBottom: '1px solid rgba(34, 197, 94, 0.2)',
        padding: '1rem',
        marginBottom: '1rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(20px)'
      }}>
        <div className="header-container" style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '0 1rem',
          position: 'relative'
        }}>
          {/* Back Button - Far Left */}
          <button
            className="back-button"
            onClick={() => navigateTo('pharma-homepage')}
            style={{
              background: 'linear-gradient(135deg, #1B5E20 0%, #0D4A14 100%)',
              color: 'white',
              border: 'none',
              padding: '0.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(27, 94, 32, 0.3)',
              transition: 'all 0.3s ease',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '0',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(27, 94, 32, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(27, 94, 32, 0.3)';
            }}
          >
            ←
          </button>

           {/* Center Heading - Absolutely Centered */}
           <div className="header-title" style={{
             position: 'absolute',
             left: '50%',
             transform: 'translateX(-50%)',
             fontSize: '1.5rem',
             fontWeight: '700',
             color: '#1B5E20',
             letterSpacing: '0.5px',
             textShadow: '0 2px 4px rgba(27, 94, 32, 0.1)',
             whiteSpace: 'nowrap'
           }}>
             {activeTab === 'restock' ? 'RESTOCK MEDICATIONS' : 
              activeTab === 'checkout' ? 'CHECKOUT & BILLING' : 
              'INVENTORY MANAGEMENT'}
           </div>

          {/* Tab Switcher - Right Side with padding */}
          <div className="tab-switcher" style={{
            position: 'relative',
            display: 'flex',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '2px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden',
            width: '350px',
            maxWidth: '50vw',
            marginLeft: 'auto',
            marginRight: '2rem',
            flexShrink: 0
          }}>
            {/* Sliding Background */}
            <div
              style={{
                position: 'absolute',
                top: '2px',
                left: getTabPosition(),
                width: 'calc(33.33% - 2px)',
                height: 'calc(100% - 4px)',
                background: 'linear-gradient(135deg, #225fe1 0%, #1e40af 100%)',
                borderRadius: '10px',
                transition: isAnimating ? 'all 0.7s cubic-bezier(0.25, 1.7, 0.35, 0.8)' : 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(34, 95, 225, 0.3)',
                zIndex: 1,
                transform: isAnimating ? 'scale(1.05)' : 'scale(1)',
                filter: isAnimating ? 'brightness(1.15)' : 'brightness(1)'
              }}
            />
            
            <button
              onClick={() => triggerAnimation('restock')}
              className="tab-button"
              style={{
                position: 'relative',
                zIndex: 2,
                padding: '0.5rem 0.75rem',
                borderRadius: '10px',
                border: 'none',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                background: 'transparent',
                color: getButtonColor('restock'),
                transition: 'transform 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), color 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8)',
                width: '33.33%',
                transform: activeTab === 'restock' && isAnimating ? 'scale(1.08)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'restock') {
                  e.currentTarget.style.color = '#225fe1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'restock') {
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              RESTOCK
            </button>
            
            <button
              onClick={() => triggerAnimation('checkout')}
              className="tab-button"
              style={{
                position: 'relative',
                zIndex: 2,
                padding: '0.5rem 0.75rem',
                borderRadius: '10px',
                border: 'none',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                background: 'transparent',
                color: getButtonColor('checkout'),
                transition: 'transform 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), color 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8)',
                width: '33.33%',
                transform: activeTab === 'checkout' && isAnimating ? 'scale(1.08)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'checkout') {
                  e.currentTarget.style.color = '#225fe1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'checkout') {
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              CHECKOUT/BILL
            </button>
            
            <button
              onClick={() => triggerAnimation('inventory')}
              className="tab-button"
              style={{
                position: 'relative',
                zIndex: 2,
                padding: '0.5rem 0.75rem',
                borderRadius: '10px',
                border: 'none',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                background: 'transparent',
                color: getButtonColor('inventory'),
                transition: 'transform 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), color 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8)',
                width: '33.33%',
                transform: activeTab === 'inventory' && isAnimating ? 'scale(1.08)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'inventory') {
                  e.currentTarget.style.color = '#225fe1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'inventory') {
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              INVENTORY
            </button>
          </div>
        </div>
      </div>

      {/* Conditional Content Based on Active Tab */}
      {activeTab === 'restock' && (
        <>
      {/* Modern Table Container */}
      <div 
        className="table-container" 
        style={{
          margin: '0 1rem 2rem 1rem',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.08), 0 10px 25px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(25px)',
        width: 'calc(100% - 2rem)',
        position: 'relative',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}
      >

        {/* Action Buttons Row */}
        <div className="action-buttons" style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(5, 150, 105, 0.03) 100%)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative Background Elements */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-10%',
            width: '120%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.02) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            top: '-30%',
            right: '-5%',
            width: '80%',
            height: '160%',
            background: 'radial-gradient(circle, rgba(5, 150, 105, 0.02) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          {/* Left Side - CSV Upload Button */}
          <label className="action-button csv-upload-button" style={{
            background: isUploadingCSV 
              ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            border: 'none',
            padding: '0.875rem 1.75rem',
            borderRadius: '10px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: isUploadingCSV ? 'not-allowed' : 'pointer',
            boxShadow: isUploadingCSV 
              ? '0 4px 12px rgba(156, 163, 175, 0.25)'
              : '0 4px 12px rgba(59, 130, 246, 0.25)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 1,
            opacity: isUploadingCSV ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!isUploadingCSV) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.35)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isUploadingCSV) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
            }
          }}
          >
            {isUploadingCSV ? (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            )}
            {isUploadingCSV ? 'Uploading...' : 'Upload CSV'}
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              disabled={isUploadingCSV}
              style={{
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                pointerEvents: isUploadingCSV ? 'none' : 'none'
              }}
            />
          </label>

          {/* Middle - Distributor Name Input */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            maxWidth: '300px',
            minWidth: '200px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#6b7280', flexShrink: 0 }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <input
              type="text"
              placeholder="Select distributor from list below"
              value={selectedDistributor?.name || ''}
              onChange={(e) => {
                if (selectedDistributor) {
                  setSelectedDistributor({
                    ...selectedDistributor,
                    name: e.target.value
                  });
                } else {
                  setSelectedDistributor({
                    id: 0,
                    name: e.target.value,
                    contact: '',
                    phone: '',
                    email: '',
                    address: '',
                    gstNumber: '',
                    settledAmount: 0,
                    unsettledAmount: 0,
                    overpaidAmount: 0
                  });
                  // Show warning for manual entry
                  console.warn('⚠️ Manual distributor entry detected. Please select from the list below for valid distributor ID.');
                }
              }}
              style={{
                flex: 1,
                padding: '0.875rem 1rem',
                border: '2px solid rgba(226, 232, 240, 0.6)',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#374151',
                outline: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxSizing: 'border-box',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#059669';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                e.currentTarget.style.boxShadow = '0 0 0 4px rgba(5, 150, 105, 0.1), 0 4px 16px rgba(5, 150, 105, 0.15)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.02)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            />
          </div>

          {/* Right Side - Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* View Saved Purchases Button */}
          <button
              onClick={fetchPendingPurchases}
              disabled={isLoadingPurchases}
            className="action-button"
            style={{
                background: isLoadingPurchases 
                  ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              padding: '0.875rem 1.75rem',
              borderRadius: '10px',
              fontSize: '0.9rem',
              fontWeight: '600',
                cursor: isLoadingPurchases ? 'not-allowed' : 'pointer',
                boxShadow: isLoadingPurchases 
                  ? '0 4px 12px rgba(156, 163, 175, 0.25)'
                  : '0 4px 12px rgba(59, 130, 246, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
                position: 'relative',
                overflow: 'hidden',
                zIndex: 1,
                opacity: isLoadingPurchases ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!isLoadingPurchases) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.35)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoadingPurchases) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                }
              }}
            >
              {isLoadingPurchases ? (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {isLoadingPurchases ? 'Loading...' : 'View Saved Purchases'}
            </button>

            {/* Add Field Button */}
          <button
            onClick={addNewRow}
            className="action-button"
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '16px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 25px rgba(34, 197, 94, 0.3), 0 4px 12px rgba(34, 197, 94, 0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              position: 'relative',
              overflow: 'hidden',
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(34, 197, 94, 0.4), 0 8px 20px rgba(34, 197, 94, 0.3)';
              e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(34, 197, 94, 0.3), 0 4px 12px rgba(34, 197, 94, 0.2)';
              e.currentTarget.style.background = 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            + Add Field
          </button>
          </div>
        </div>

        {/* Fixed Table Header Container (hidden on mobile) */}
        {!isMobile && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
          overflow: 'hidden'
        }}>
          {/* Scrollable Header Content */}
          <div 
            ref={setHeaderScrollRef}
            className="table-header-scrollable"
            onScroll={handleHeaderScroll}
            style={{
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div className="table-header" style={{
              display: 'grid',
          gridTemplateColumns: `60px 200px 150px 120px 120px 120px 80px 120px 100px 100px 80px 120px 120px 100px ${dynamicFields.map(() => '120px').join(' ')}`,
              gap: '1rem',
              padding: '1rem',
              color: 'white',
              fontWeight: '600',
              fontSize: '0.9rem',
              textAlign: 'center',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              minWidth: '1400px',
              width: '100%',
              boxSizing: 'border-box',
              alignItems: 'center',
              lineHeight: '1.2',
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>S. NO</div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'medicineName')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['medicineName'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['medicineName'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['medicineName'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['medicineName'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                {fieldMapping['medicineName'] ? fieldMapping['medicineName'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Medicine Name'}
                  </span>
                  {fieldMapping['medicineName'] && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFieldMapping('medicineName');
                      }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title="Clear field mapping"
                    >
                      ×
                    </button>
                  )}
                </div>
            </div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'composition')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['composition'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['composition'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['composition'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['composition'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                {fieldMapping['composition'] ? fieldMapping['composition'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Composition Name'}
                  </span>
                  {fieldMapping['composition'] && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFieldMapping('composition');
                      }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title="Clear field mapping"
                    >
                      ×
                    </button>
                  )}
                </div>
          </div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'brandName')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['brandName'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['brandName'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['brandName'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['brandName'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                {fieldMapping['brandName'] ? fieldMapping['brandName'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Brand Name'}
              </div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'batchNumber')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['batchNumber'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['batchNumber'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['batchNumber'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['batchNumber'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                {fieldMapping['batchNumber'] ? fieldMapping['batchNumber'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Batch Number'}
                  </span>
                  {fieldMapping['batchNumber'] && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFieldMapping('batchNumber');
                      }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title="Clear field mapping"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'quantity')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['quantity'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['quantity'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['quantity'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['quantity'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                {fieldMapping['quantity'] ? fieldMapping['quantity'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Quantity'}
                  </span>
                  {fieldMapping['quantity'] && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFieldMapping('quantity');
                      }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title="Clear field mapping"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Quantity Type</div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'tabletsPerStrip')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['tabletsPerStrip'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['tabletsPerStrip'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
              >
                {fieldMapping['tabletsPerStrip'] ? 'Tablets Per Strip' : 'Tablets Per Strip'}
              </div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'costPrice')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['costPrice'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['costPrice'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['costPrice'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['costPrice'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                {fieldMapping['costPrice'] ? fieldMapping['costPrice'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Cost Price'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>MRP</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>GST Rate</div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'expiryDate')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['expiryDate'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['expiryDate'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging) {
                    e.currentTarget.style.backgroundColor = fieldMapping['expiryDate'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent';
                    e.currentTarget.style.borderColor = fieldMapping['expiryDate'] ? 'rgba(16, 185, 129, 0.5)' : 'transparent';
                  }
                }}
              >
                {fieldMapping['expiryDate'] ? fieldMapping['expiryDate'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Expiry Date'}
              </div>
              <div 
                onDragOver={handleFieldDragOver}
                onDrop={(e) => handleFieldDrop(e, 'purchaseDate')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  backgroundColor: fieldMapping['purchaseDate'] ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: fieldMapping['purchaseDate'] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed transparent',
                  cursor: 'pointer'
                }}
              >
                {fieldMapping['purchaseDate'] ? 'Purchase Date' : 'Purchase Date'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Line Total</div>
              
              {/* Dynamic Fields */}
              {dynamicFields.map((fieldKey) => (
                <div 
                  key={fieldKey}
                  onDragOver={handleFieldDragOver}
                  onDrop={(e) => handleFieldDrop(e, fieldKey)}
                  onClick={() => startEditingField(fieldKey)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease',
                    backgroundColor: fieldMapping[fieldKey] ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                    border: fieldMapping[fieldKey] ? '2px dashed rgba(16, 185, 129, 0.5)' : '2px dashed rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    minHeight: '40px'
                  }}
                  onMouseEnter={(e) => {
                    if (isDragging) {
                      e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                    } else {
                      e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isDragging) {
                      e.currentTarget.style.backgroundColor = fieldMapping[fieldKey] ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.borderColor = fieldMapping[fieldKey] ? 'rgba(16, 185, 129, 0.5)' : 'rgba(255, 255, 255, 0.3)';
                    } else {
                      e.currentTarget.style.backgroundColor = fieldMapping[fieldKey] ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                >
                  {editingField === fieldKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', width: '100%' }}>
                      <input
                        type="text"
                        value={editingFieldName}
                        onChange={(e) => setEditingFieldName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            saveFieldName(fieldKey);
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        onBlur={() => saveFieldName(fieldKey)}
                        autoFocus
                        style={{
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: 'white',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          textAlign: 'center',
                          width: '100%',
                          padding: '0.25rem'
                        }}
                      />
                    </div>
                  ) : (
                    fieldMapping[fieldKey] ? fieldMapping[fieldKey].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Add Field'
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Scrollable Table Content - Only Rows (hidden on mobile) */}
        {!isMobile && (
        <div 
          ref={setContentScrollRef}
          className="table-scrollable-content"
          onScroll={handleScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 400px)',
            position: 'relative',
            margin: '0 1rem'
          }}
        >
          
          {/* Modern Table Rows with Proper Alignment */}
        {rows.map((row, index) => (
          <div key={row.id} className="table-row" style={{
            display: 'grid',
            gridTemplateColumns: `60px 200px 150px 120px 120px 120px 80px 120px 100px 100px 80px 120px 120px 100px ${dynamicFields.map(() => '120px').join(' ')}`,
            gap: '1rem',
            padding: '1rem',
            background: index % 2 === 0 ? 'rgba(16, 185, 129, 0.02)' : 'rgba(16, 185, 129, 0.05)',
            borderBottom: index === rows.length - 1 ? 'none' : '1px solid rgba(16, 185, 129, 0.1)',
            fontSize: '0.85rem',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'visible',
            marginBottom: focusedQuantityRow === row.id ? '2.5rem' : (focusedMedicineRow === row.id ? '220px' : '0'),
            animation: `slideInUp 0.4s ease-out ${index * 0.05}s both`,
            minWidth: '1400px', // Match container minimum width
            width: '100%',
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
            e.currentTarget.style.transform = 'translateX(2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = index % 2 === 0 ? 'rgba(16, 185, 129, 0.02)' : 'rgba(16, 185, 129, 0.05)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}>
            {/* Row Number with Remove Button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
              color: '#64748b',
              gap: '0.5rem'
            }}>
              <span>{index + 1}</span>
              {rows.length > 1 && (
                <button
                  onClick={() => removeRow(row.id)}
                  style={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    width: '24px',
                    height: '24px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 6px rgba(220, 38, 38, 0.25)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(220, 38, 38, 0.35)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(220, 38, 38, 0.25)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                  }}
                  title="Remove row"
                >
                  ×
                </button>
              )}
            </div>
                
          {/* Medicine Name with API Search */}
          <div style={{ 
            textAlign: 'left', 
            position: 'relative'
          }}>
            <input 
              type="text" 
              placeholder="Enter medicine name"
                value={row.data.medicineName}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log('🔤 Medicine name input changed:', value, 'for row:', row.id);
                  handleRowInputChange(row.id, 'medicineName', value);
                  
                  // Call API when typing
                  if (value.trim().length > 0) {
                    console.log('🚀 Calling searchMedicines with:', value);
                    searchMedicines(value, row.id);
                  } else {
                    console.log('🧹 Clearing medicine results');
                    setMedicineResults(prev => ({ ...prev, [row.id]: [] }));
                  }
                }}
              style={{
                width: '100%',
                padding: '0.6rem',
                border: '2px solid rgba(226, 232, 240, 0.6)',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxSizing: 'border-box',
                position: 'relative'
                }}
                onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 12px rgba(59, 130, 246, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  setFocusedMedicineRow(row.id);
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                  // Only close if the blur is not caused by clicking on the dropdown
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (!relatedTarget || !relatedTarget.closest('[data-medicine-dropdown]')) {
                    setTimeout(() => setFocusedMedicineRow(null), 150);
                  }
                }}
                onKeyDown={(e) => {
                  // Only handle keyboard navigation when dropdown is open
                  if (focusedMedicineRow !== row.id || !medicineResults[row.id]?.length) {
                    return;
                  }

                  const currentIndex = selectedIndex[row.id] || 0;
                  const maxIndex = medicineResults[row.id].length - 1;

                  switch (e.key) {
                    case 'ArrowDown':
                      e.preventDefault();
                      const nextIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
                      setSelectedIndex(prev => ({ ...prev, [row.id]: nextIndex }));
                      break;
                    
                    case 'ArrowUp':
                      e.preventDefault();
                      const prevIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
                      setSelectedIndex(prev => ({ ...prev, [row.id]: prevIndex }));
                      break;
                    
                    case 'Enter':
                      e.preventDefault();
                      const selectedMedicine = medicineResults[row.id][currentIndex];
                      if (selectedMedicine) {
                        handleRowInputChange(
                          row.id,
                          'medicineName',
                          selectedMedicine.name || selectedMedicine.medicine_name || selectedMedicine.medicineName || selectedMedicine.Name || ''
                        );
                        handleRowInputChange(
                          row.id,
                          'composition',
                          selectedMedicine.medicine_composition || selectedMedicine.salt_composition || selectedMedicine.Salt_Composition || selectedMedicine.composition || selectedMedicine.generic_name || selectedMedicine.type || ''
                        );
                        handleRowInputChange(
                          row.id,
                          'brandName',
                          selectedMedicine.medicine_company || selectedMedicine.manufacturer || selectedMedicine.Manufacturer || selectedMedicine.company || selectedMedicine.brand_name || ''
                        );
                        setMedicineResults(prev => ({ ...prev, [row.id]: [] }));
                        setFocusedMedicineRow(null);
                      }
                      break;
                    
                    case 'Escape':
                      e.preventDefault();
                      setFocusedMedicineRow(null);
                      setMedicineResults(prev => ({ ...prev, [row.id]: [] }));
                      break;
                  }
                }}
            />
            
            {/* Desktop Medicine Dropdown removed */}
          </div>
                
          {/* Composition Name */}
          <div style={{ textAlign: 'left' }}>
            <input 
              type="text" 
              placeholder="Enter composition"
                value={row.data.composition}
                onChange={(e) => handleRowInputChange(row.id, 'composition', e.target.value)}
              style={{
                width: '100%',
                padding: '0.6rem',
                border: '2px solid rgba(226, 232, 240, 0.6)',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxSizing: 'border-box',
                position: 'relative'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 12px rgba(59, 130, 246, 0.15)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            />
          </div>
                
          {/* Brand Name */}
          <div style={{ textAlign: 'left' }}>
            <input 
              type="text" 
              placeholder="Enter brand name"
                value={row.data.brandName}
                onChange={(e) => handleRowInputChange(row.id, 'brandName', e.target.value)}
              style={{
                width: '100%',
                padding: '0.6rem',
                border: '2px solid rgba(226, 232, 240, 0.6)',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxSizing: 'border-box',
                position: 'relative'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 12px rgba(59, 130, 246, 0.15)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            />
            
            {/* Medicine Dropdown */}
            {focusedMedicineRow === row.id && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                margin: '0.5rem 0',
                padding: '0.5rem',
                border: '2px solid #3b82f6',
                borderRadius: '8px',
                backgroundColor: 'white',
                maxHeight: '350px',
                overflowY: 'auto',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                zIndex: 2147483647,
                width: '100%',
                minWidth: '300px',
                boxSizing: 'border-box',
                transform: 'translateY(0)'
              }} data-dropdown-container={row.id} data-medicine-dropdown>
                {medicineResults[row.id]?.length > 0 ? (
                  medicineResults[row.id].map((medicine, index) => {
                    const isSelected = index === (selectedIndex[row.id] || 0);
                    
                    return (
                      <div
                        key={medicine.id || index}
                        data-dropdown-item
                        data-dropdown-index={index}
                        onClick={() => {
                          handleRowInputChange(
                            row.id,
                            'medicineName',
                            medicine.name || medicine.medicine_name || medicine.medicineName || medicine.Name || ''
                          );
                          handleRowInputChange(
                            row.id,
                            'composition',
                            medicine.medicine_composition || medicine.salt_composition || medicine.Salt_Composition || medicine.composition || medicine.generic_name || medicine.type || ''
                          );
                          handleRowInputChange(
                            row.id,
                            'brandName',
                            medicine.medicine_company || medicine.manufacturer || medicine.Manufacturer || medicine.company || medicine.brand_name || ''
                          );
                          setMedicineResults(prev => ({ ...prev, [row.id]: [] }));
                          setFocusedMedicineRow(null);
                        }}
                        onMouseEnter={() => {
                          // Update selection on hover for better UX
                          setSelectedIndex(prev => ({ ...prev, [row.id]: index }));
                        }}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#3b82f6' : 'white',
                          color: isSelected ? 'white' : '#374151',
                          borderBottom: index < medicineResults[row.id].length - 1 ? '1px solid #e5e7eb' : 'none',
                          fontSize: '0.875rem',
                          fontWeight: isSelected ? '600' : '500',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          position: 'relative',
                          borderRadius: '4px',
                          margin: '2px 0',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            marginBottom: '2px',
                            color: isSelected ? 'white' : '#1f2937'
                          }}>
                            {medicine.name || medicine.medicine_name || medicine.medicineName || medicine.Name || 'Unknown Medicine'}
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            opacity: 0.8,
                            color: isSelected ? 'rgba(255, 255, 255, 0.9)' : '#6b7280',
                            marginBottom: '2px'
                          }}>
                            <strong>Composition:</strong> {medicine.salt_composition || medicine.Salt_Composition || medicine.medicine_composition || medicine.composition || medicine.saltComposition || medicine.generic_name || medicine.type || 'Not available'}
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            opacity: 0.8,
                            color: isSelected ? 'rgba(255, 255, 255, 0.9)' : '#6b7280'
                          }}>
                            <strong>Manufacturer:</strong> {medicine.manufacturer || medicine.Manufacturer || medicine.medicine_company || medicine.company || medicine.manufacturerName || medicine.brand_name || 'Not available'}
                          </div>
                        </div>
                        {isSelected && (
                          <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    {row.data.medicineName.trim().length > 0 ? 'Searching medicines...' : 'Type to search medicines'}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <input 
              type="text" 
              placeholder="Batch"
                value={row.data.batchNumber}
                onChange={(e) => handleRowInputChange(row.id, 'batchNumber', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
                minHeight: '40px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
                
          {/* Quantity */}
          <div>
            <input 
              type="number" 
              placeholder="Qty"
                value={row.data.quantity}
                onChange={(e) => handleRowInputChange(row.id, 'quantity', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
                
          {/* Quantity Type */}
          <div>
            <input 
              type="text" 
              placeholder="Type"
              value={row.data.quantityType || ''}
              onChange={(e) => handleRowInputChange(row.id, 'quantityType', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
                
          {/* Tablets Per Strip */}
          <div>
            <input 
              type="number" 
              placeholder="Per Strip"
              value={row.data.tabletsPerStrip}
              onChange={(e) => handleRowInputChange(row.id, 'tabletsPerStrip', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
                
          {/* Cost Price */}
          <div>
            <input 
              type="number" 
              placeholder="₹0.00"
                value={row.data.costPrice}
                onChange={(e) => handleRowInputChange(row.id, 'costPrice', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
                minHeight: '40px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
                
          {/* MRP */}
          <div>
            <input 
              type="number" 
              placeholder="₹0.00"
                value={row.data.mrp}
                onChange={(e) => handleRowInputChange(row.id, 'mrp', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
                minHeight: '40px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
                
          {/* GST Rate */}
          <div>
            <input 
              type="number" 
              placeholder="%"
                value={row.data.gstRate}
                onChange={(e) => handleRowInputChange(row.id, 'gstRate', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
                minHeight: '40px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
                
          {/* Expiry Date */}
          <div>
            <input 
              type="date" 
                value={row.data.expiryDate}
                onChange={(e) => handleRowInputChange(row.id, 'expiryDate', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
                minHeight: '40px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
                
          {/* Purchase Date */}
          <div>
            <input 
              type="date" 
              value={row.data.purchaseDate}
              onChange={(e) => handleRowInputChange(row.id, 'purchaseDate', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '2px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.8rem',
                outline: 'none',
                color: '#374151',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
                minHeight: '40px'
              }}
            />
          </div>
                
          
          {/* Line Total */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}>
            <div style={{
              width: '100%',
              padding: '0.5rem',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              fontSize: '0.8rem',
              color: '#059669',
              fontWeight: '600',
              textAlign: 'center',
              boxSizing: 'border-box',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              ₹{(() => {
                const quantity = parseFloat(row.data.quantity) || 0;
                const costPrice = parseFloat(row.data.costPrice) || 0;
                const gstRate = parseFloat(row.data.gstRate) || 0;
                
                // Calculate base amount
                const baseAmount = quantity * costPrice;
                
                // Calculate GST amount
                const gstAmount = (baseAmount * gstRate) / 100;
                
                // Calculate total including GST
                const lineTotal = baseAmount + gstAmount;
                
                return lineTotal.toFixed(2);
              })()}
            </div>
          </div>
          {/* Dynamic Fields */}
          {dynamicFields.map((fieldKey) => (
            <div key={fieldKey} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}>
              <input 
                type="text" 
                placeholder={fieldMapping[fieldKey] ? fieldMapping[fieldKey].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Add Field'}
                value={row.data[fieldKey] || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRowInputChange(row.id, fieldKey, e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
              onFocus={(e) => {
                  e.stopPropagation();
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
              }}
              onBlur={(e) => {
                  e.stopPropagation();
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = 'none';
              }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.8rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  minHeight: '40px',
                  position: 'relative',
                  zIndex: 10
                }}
            />
          </div>
          ))}
        </div>
        ))}
      </div>
        )}

        {/* Mobile Vertical Cards (<= 780px) */}
        {isMobile && (
          <div style={{
            display: 'grid',
            gap: '0.75rem',
            padding: '0.75rem 1rem'
          }}>
                {rows.map((row, index) => (
              <div key={row.id} style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.15)',
                boxShadow: '0 6px 16px rgba(16,185,129,0.08)',
                padding: '0.75rem 0.75rem 0.5rem 0.75rem',
                position: 'relative',
                overflow: 'visible'
              }}>
                {/* Card Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
                  paddingBottom: '0.5rem'
                }}>
                  <div style={{
                    fontWeight: 700,
                    color: '#059669'
                  }}>Item {index + 1}</div>
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(row.id)}
                      style={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}
                    >Remove</button>
                  )}
                </div>

                {/* Label / Value rows */}
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {/* Medicine Name */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', position: 'relative' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600 }}>Medicine Name</div>
                    <input
                      type="text"
                      value={row.data.medicineName}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleRowInputChange(row.id, 'medicineName', value);
                        if (value.trim().length > 0) {
                          searchMedicines(value, row.id);
                        } else {
                          setMedicineResults(prev => ({ ...prev, [row.id]: [] }));
                        }
                      }}
                      onFocus={() => setFocusedMedicineRow(row.id)}
                      onBlur={(e) => {
                        const relatedTarget = (e.relatedTarget as HTMLElement) || null;
                        if (!relatedTarget || !relatedTarget.closest('[data-medicine-dropdown]')) {
                          setTimeout(() => setFocusedMedicineRow(null), 150);
                        }
                      }}
                      placeholder="Enter medicine name"
                      style={{
                        flex: 1,
                        padding: '0.6rem',
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    {/* Mobile Medicine Dropdown - Styled */}
                    {(() => {
                      const results = medicineResults[row.id] || [];
                      const shouldShow = results.length >= 0 && focusedMedicineRow === row.id;
                      return shouldShow;
                    })() && (
                      <div 
                        data-medicine-dropdown
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 2147483647,
                          background: '#ffffff',
                          border: '2px solid #3b82f6',
                          borderRadius: '10px',
                          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                          maxHeight: '220px',
                          overflowY: 'auto',
                          marginTop: '6px',
                          width: '100%'
                        }}
                      >
                        <div style={{
                          padding: '10px 14px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}>
                          <div style={{
                            width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px'
                          }}>💊</div>
                          <div style={{ flex: 1 }}>Select Medicine ({(medicineResults[row.id] || []).length} results)</div>
                        </div>
                        <div>
                          {((medicineResults[row.id] || []).length === 0) && (
                            <div style={{ padding: '14px', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', fontWeight: 600 }}>No medicines found</div>
                          )}
                          {(medicineResults[row.id] || []).map((medicine, index) => (
                            <div
                              key={medicine.id || index}
                              onClick={() => {
                                handleRowInputChange(row.id, 'medicineName', medicine.Name || medicine.name || medicine.medicine_name || '');
                                handleRowInputChange(row.id, 'composition', medicine.Salt_Composition || medicine.type || medicine.composition || medicine.generic_name || '');
                                handleRowInputChange(row.id, 'brandName', medicine.Manufacturer || medicine.company || medicine.medicine_company || medicine.brand_name || '');
                                setMedicineResults(prev => ({ ...prev, [row.id]: [] }));
                                setFocusedMedicineRow(null);
                              }}
                              style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                color: '#374151',
                                borderBottom: index < (medicineResults[row.id] || []).length - 1 ? '1px solid #e5e7eb' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                position: 'relative',
                                width: '100%',
                                boxSizing: 'border-box'
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'white'; }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, marginBottom: '4px', color: '#1f2937', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {medicine.Name || medicine.name || medicine.medicine_name || 'Unknown Medicine'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>
                                  {medicine.Salt_Composition || medicine.type || medicine.composition || medicine.generic_name || 'No composition'}
                                </div>
                                <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                                  {medicine.Manufacturer || medicine.company || medicine.medicine_company || medicine.brand_name || 'Unknown Manufacturer'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Composition */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Composition</div>
                    <input
                      type="text"
                      value={row.data.composition}
                      onChange={(e) => handleRowInputChange(row.id, 'composition', e.target.value)}
                      placeholder="Enter composition"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Brand Name */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Brand Name</div>
                    <input
                      type="text"
                      value={row.data.brandName}
                      onChange={(e) => handleRowInputChange(row.id, 'brandName', e.target.value)}
                      placeholder="Enter brand name"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Batch Number */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Batch Number</div>
                    <input
                      type="text"
                      value={row.data.batchNumber}
                      onChange={(e) => handleRowInputChange(row.id, 'batchNumber', e.target.value)}
                      placeholder="Batch"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Quantity */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Quantity</div>
                    <input
                      type="number"
                      value={row.data.quantity}
                      onChange={(e) => handleRowInputChange(row.id, 'quantity', e.target.value)}
                      placeholder="Qty"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Quantity Type */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Quantity Type</div>
                    <select
                      value={row.data.quantityType || 'strip'}
                      onChange={(e) => handleRowInputChange(row.id, 'quantityType', e.target.value)}
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', background: 'white' }}
                    >
                      <option value="strip">Strip</option>
                      <option value="tablet">Tablet</option>
                      <option value="bottle">Bottle</option>
                      <option value="vial">Vial</option>
                      <option value="syringe">Syringe</option>
                      <option value="tube">Tube</option>
                      <option value="packet">Packet</option>
                      <option value="box">Box</option>
                    </select>
                  </div>

                  {/* Cost Price */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Cost Price (₹)</div>
                    <input
                      type="number"
                      value={row.data.costPrice}
                      onChange={(e) => handleRowInputChange(row.id, 'costPrice', e.target.value)}
                      placeholder="₹0.00"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* MRP */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>MRP (₹)</div>
                    <input
                      type="number"
                      value={row.data.mrp}
                      onChange={(e) => handleRowInputChange(row.id, 'mrp', e.target.value)}
                      placeholder="₹0.00"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* GST Rate */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>GST Rate (%)</div>
                    <input
                      type="number"
                      value={row.data.gstRate}
                      onChange={(e) => handleRowInputChange(row.id, 'gstRate', e.target.value)}
                      placeholder="%"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Expiry Date */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Expiry Date</div>
                    <input
                      type="date"
                      value={row.data.expiryDate}
                      onChange={(e) => handleRowInputChange(row.id, 'expiryDate', e.target.value)}
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Tablets Per Strip */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Tablets Per Strip</div>
                    <input
                      type="number"
                      value={row.data.tabletsPerStrip}
                      onChange={(e) => handleRowInputChange(row.id, 'tabletsPerStrip', e.target.value)}
                      placeholder="Per Strip"
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>

                  {/* Purchase Date */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>Purchase Date</div>
                    <input
                      type="date"
                      value={row.data.purchaseDate}
                      onChange={(e) => handleRowInputChange(row.id, 'purchaseDate', e.target.value)}
                      style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </div>


                  {/* Line Total (read-only) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontWeight: 700, minWidth: '45%' }}>Line Total</div>
                    <div style={{
                      flex: 1,
                      padding: '0.6rem',
                      border: '1.5px solid rgba(16,185,129,0.25)',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(5,150,105,0.08) 100%)',
                      color: '#059669',
                      fontWeight: 700,
                      textAlign: 'right'
                    }}>
                      {(() => {
                        const quantity = parseFloat(row.data.quantity) || 0;
                        const costPrice = parseFloat(row.data.costPrice) || 0;
                        const gstRate = parseFloat(row.data.gstRate) || 0;
                        const baseAmount = quantity * costPrice;
                        const gstAmount = (baseAmount * gstRate) / 100;
                        const lineTotal = baseAmount + gstAmount;
                        return `₹${lineTotal.toFixed(2)}`;
                      })()}
                    </div>
                  </div>

                  {/* Dynamic Fields (if any) */}
                  {dynamicFields.map((fieldKey) => (
                    <div key={fieldKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                      <div style={{ color: '#6b7280', fontWeight: 600, minWidth: '45%' }}>
                        {(fieldMapping[fieldKey] ? fieldMapping[fieldKey] : 'Add Field').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </div>
                      <input
                        type="text"
                        value={row.data[fieldKey] || ''}
                        onChange={(e) => handleRowInputChange(row.id, fieldKey, e.target.value)}
                        placeholder={fieldMapping[fieldKey] ? fieldMapping[fieldKey].replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : 'Add Field'}
                        style={{ flex: 1, padding: '0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Pending Purchases Display */}
      {showPendingPurchases && (
      <div className="pending-purchases-section" style={{
        margin: '0 2rem 1rem 2rem',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
        borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.1)'
        }}>
          <div style={{
        display: 'flex',
        alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1.3rem',
        fontWeight: '700',
              color: '#1f2937',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
        <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                </svg>
              </div>
              Saved Purchases ({pendingPurchases.length})
            </h3>
            
            <button
              onClick={() => setShowPendingPurchases(false)}
              style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 3px 10px rgba(239, 68, 68, 0.3)',
          display: 'flex',
          alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.3)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Close
            </button>
          </div>

          {/* Error Display */}
          {purchasesError && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              color: '#dc2626',
              fontSize: '0.9rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
                Error: {purchasesError}
      </div>
            </div>
          )}

          {/* No Data Message */}
          {!purchasesError && pendingPurchases.length === 0 && (
        <div style={{ 
              background: 'rgba(156, 163, 175, 0.1)',
              border: '1px solid rgba(156, 163, 175, 0.2)',
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ margin: '0 auto 1rem auto', opacity: 0.5 }}>
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
              </svg>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                No Saved Purchases Found
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                There are currently no pending purchases saved in the system.
              </div>
            </div>
          )}

          {/* Purchases List */}
          {!purchasesError && pendingPurchases.length > 0 && (
            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {pendingPurchases.map((purchase: any, index: number) => (
                <div key={purchase.id || index} className="pending-purchase-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.1)',
                  padding: '1.5rem',
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 92, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.1)';
                }}>
                  {/* Purchase Header */}
                  <div className="pending-purchase-header" style={{
          display: 'flex',
                    justifyContent: 'space-between',
          alignItems: 'center',
                    marginBottom: '1rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid rgba(139, 92, 246, 0.1)'
                  }}>
                    <div>
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#1f2937',
                        margin: '0 0 0.25rem 0'
                      }}>
                        {purchase.pending_id || `Purchase #${purchase.id || purchase.purchase_id || index + 1}`}
                      </h4>
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        marginBottom: '0.25rem'
                      }}>
                        Created: {purchase.created_at ? new Date(purchase.created_at).toLocaleString() : 'Date not available'}
                      </div>
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#9ca3af'
                      }}>
                        Created by User ID: {purchase.created_by || 'Unknown'}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
          gap: '0.5rem',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        background: purchase.status === 'pending_confirmation' 
                          ? 'rgba(245, 158, 11, 0.1)' 
                          : 'rgba(34, 197, 94, 0.1)',
                        color: purchase.status === 'pending_confirmation' 
                          ? '#d97706' 
                          : '#16a34a',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>{purchase.status ? purchase.status.replace('_', ' ') : 'Pending'}</span>
                        {purchase.status === 'pending_confirmation' && purchase.total_amount && purchase.settled_amount !== undefined && (
                          <span style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#dc2626',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '700'
                          }}>
                            Unsettled: ₹{(purchase.total_amount - (purchase.settled_amount || 0)).toFixed(2)}
                          </span>
                        )}
        </div>
                      {purchase.total_amount && (
                        <div style={{
                          background: 'rgba(139, 92, 246, 0.1)',
                          color: '#8b5cf6',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.125rem'
                        }}>
                          <span>Total: ₹{purchase.total_amount}</span>
                          {purchase.settled_amount !== undefined && (
                            <span style={{
                              fontSize: '0.7rem',
                              color: '#10b981',
                              fontWeight: '500'
                            }}>
                              Settled: ₹{purchase.settled_amount}
                            </span>
                          )}
                        </div>
                      )}
        <button
                        onClick={() => openEditModal(purchase)}
          style={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
            fontWeight: '600',
            cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 10px rgba(59, 130, 246, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(59, 130, 246, 0.3)';
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Edit
                      </button>
                      
                      <button
                        onClick={() => confirmPendingPurchase(purchase)}
                        disabled={confirmingPurchaseId === purchase.pending_id}
                        style={{
                          background: confirmingPurchaseId === purchase.pending_id 
                            ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: confirmingPurchaseId === purchase.pending_id ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: confirmingPurchaseId === purchase.pending_id 
                            ? '0 2px 6px rgba(156, 163, 175, 0.3)'
                            : '0 2px 6px rgba(16, 185, 129, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
                          opacity: confirmingPurchaseId === purchase.pending_id ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
                          if (confirmingPurchaseId !== purchase.pending_id) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 10px rgba(16, 185, 129, 0.4)';
                          }
          }}
          onMouseLeave={(e) => {
                          if (confirmingPurchaseId !== purchase.pending_id) {
            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(16, 185, 129, 0.3)';
                          }
                        }}
                      >
                        {confirmingPurchaseId === purchase.pending_id ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            Confirming...
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 12l2 2 4-4"/>
                              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
          </svg>
                            Confirm
                          </>
                        )}
        </button>
                    </div>
      </div>

                  {/* Items List */}
                  {purchase.items && purchase.items.length > 0 && (
                    <div>
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"/>
                        </svg>
                        Items ({purchase.items.length})
                      </div>
                      <div style={{
                        display: 'grid',
                        gap: '0.5rem'
                      }}>
                        {purchase.items.map((item: any, itemIndex: number) => (
                          <div key={itemIndex} style={{
                            background: 'rgba(139, 92, 246, 0.05)',
                            borderRadius: '8px',
                            padding: '0.75rem',
                            border: '1px solid rgba(139, 92, 246, 0.1)',
                            fontSize: '0.85rem'
                          }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                              gap: '1rem',
                              alignItems: 'center'
                            }}>
                              <div>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                  {item.medicine_name || item.name || 'Unknown Medicine'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  {item.supplier_name || item.medicine_company || item.company || 'Unknown Supplier'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontStyle: 'italic' }}>
                                  Batch: {item.batch_number || item.batch_no || 'N/A'}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '600', color: '#3b82f6' }}>
                                  {item.quantity || 'N/A'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  {item.quantity_type || 'units'}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '600', color: '#10b981' }}>
                                  ₹{item.purchase_price_per_unit || item.unit_cost || item.cost_price || 'N/A'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  Per Unit
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '600', color: '#f59e0b' }}>
                                  {item.tablets_per_strip || 'N/A'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  Per Strip
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '600', color: '#8b5cf6' }}>
                                  {item.total_tablets || 'N/A'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  Total Tablets
                                </div>
                              </div>
                            </div>
                            
                            {/* Additional Item Details */}
                            <div style={{
                              marginTop: '0.5rem',
                              paddingTop: '0.5rem',
                              borderTop: '1px solid rgba(139, 92, 246, 0.1)',
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.5rem',
                              fontSize: '0.75rem',
                              color: '#6b7280'
                            }}>
                              <div>
                                <span style={{ fontWeight: '600' }}>Purchase Date:</span> {item.purchase_date || 'N/A'}
                              </div>
                              <div>
                                <span style={{ fontWeight: '600' }}>Expiry Date:</span> {item.expiry_date || 'N/A'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Distributor Info */}
                  <div style={{
                    marginTop: '1rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid rgba(139, 92, 246, 0.1)',
                    fontSize: '0.85rem',
                    color: '#6b7280'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      <span style={{ fontWeight: '600' }}>Distributor ID:</span>
                      {purchase.distributor_id ? purchase.distributor_id : 'Not Assigned'}
                    </div>
                    {purchase.distributor && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '600' }}>Distributor Name:</span>
                        {purchase.distributor.name || purchase.distributor.company_name || 'Unknown'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
          {/* CSV Uploaded Data Display */}
          {csvUploadedItems.length > 0 && (
            <div className="csv-upload-section" style={{
          margin: '0 2rem 1rem 2rem',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
              borderRadius: '16px',
              padding: '1.5rem',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                    </svg>
                  </div>
                  CSV Uploaded Medications ({csvUploadedItems.length} items)
                </h3>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                
                <button
                  onClick={() => {
                    setFieldMapping({});
                    setDynamicFields([]);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 3px 10px rgba(245, 158, 11, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(245, 158, 11, 0.3)';
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  </svg>
                  Clear Mappings
                </button>
                  <button
                    onClick={() => {
                      setCsvUploadedItems([]);
                      setCsvUploadSummary(null);
                    setFieldMapping({});
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 3px 10px rgba(239, 68, 68, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.3)';
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Clear Data
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#10b981',
                    marginBottom: '0.25rem'
                  }}>
                    {csvUploadedItems.length}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Total Items
                  </div>
                </div>

                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(59, 130, 246, 0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#3b82f6',
                    marginBottom: '0.25rem'
                  }}>
                    {csvUploadedItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0)}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Total Quantity
                  </div>
                </div>

                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(245, 158, 11, 0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#f59e0b',
                    marginBottom: '0.25rem'
                  }}>
                    ₹{csvUploadedItems.reduce((sum: number, item: any) => {
                      const price = parseFloat(item.purchase_price_per_unit || item.unit_cost || 0);
                      const qty = parseInt(item.quantity || 0);
                      return sum + (price * qty);
                    }, 0).toFixed(2)}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Total Value
                  </div>
                </div>

                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(139, 92, 246, 0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#8b5cf6',
                    marginBottom: '0.25rem'
                  }}>
                    {new Set(csvUploadedItems.map((item: any) => item.medicine_name || item.name)).size}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Unique Items
                  </div>
                </div>
              </div>

              {/* Debug Information */}
              <div style={{
                background: 'rgba(59, 130, 246, 0.05)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.8rem',
                color: '#374151'
              }}>
                <strong>Debug Info:</strong> Total items: {csvUploadedItems.length} | 
                Fields: {csvUploadedItems.length > 0 ? Object.keys(csvUploadedItems[0]).join(', ') : 'None'} |
                Container Height: 500px | 
                Expected rows: {csvUploadedItems.length} | 
                Row height: ~60px each
              </div>

              {/* Medications Table */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
              }}>
                {/* Table Header */}
            <div 
              ref={setCsvHeaderScrollRef}
              onScroll={handleCsvHeaderScroll}
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '12px 12px 0 0'
              }}>
                <div style={{
                  display: 'grid',
                gridTemplateColumns: csvUploadedItems.length > 0 ? Object.keys(csvUploadedItems[0]).map(() => '150px').join(' ') : '150px',
                  gap: '1rem',
                  padding: '1rem',
                  color: 'white',
                  fontWeight: '600',
                fontSize: '0.9rem',
                minWidth: csvUploadedItems.length > 0 ? `${Object.keys(csvUploadedItems[0]).length * 150 + (Object.keys(csvUploadedItems[0]).length - 1) * 16}px` : '150px'
              }}>
                {csvUploadedItems.length > 0 && Object.keys(csvUploadedItems[0]).map((fieldName) => (
                  <div 
                    key={fieldName}
                    draggable
                    onDragStart={(e) => handleDragStart(e, fieldName)}
                    onDragEnd={handleDragEnd}
                    style={{
                      cursor: 'grab',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      transition: 'all 0.3s ease',
                      backgroundColor: draggedField === fieldName ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                      textAlign: 'center',
                      minWidth: '150px',
                      width: '150px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isDragging) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDragging) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                ))}
              </div>
                </div>
                
                {/* Table Body */}
            <div 
              className="csv-table-body"
              ref={setCsvBodyScrollRef}
              onScroll={handleCsvBodyScroll}
              style={{
                  maxHeight: '500px',
                  height: '500px',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  position: 'relative'
                }}>
                  {csvUploadedItems.map((medicine: any, index: number) => {
                    console.log(`Rendering CSV row ${index + 1}:`, medicine);
                    return (
                    <div key={index} style={{
                      display: 'grid',
                  gridTemplateColumns: csvUploadedItems.length > 0 ? Object.keys(csvUploadedItems[0]).map(() => '150px').join(' ') : '150px',
                      gap: '1rem',
                      padding: '1rem',
                      background: index % 2 === 0 ? 'rgba(16, 185, 129, 0.02)' : 'rgba(16, 185, 129, 0.05)',
                      borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
                      fontSize: '0.85rem',
                  transition: 'all 0.3s ease',
                  minWidth: csvUploadedItems.length > 0 ? `${Object.keys(csvUploadedItems[0]).length * 150 + (Object.keys(csvUploadedItems[0]).length - 1) * 16}px` : '150px',
                  flexShrink: 0,
                  minHeight: '60px',
                  position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                      e.currentTarget.style.transform = 'translateX(2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = index % 2 === 0 ? 'rgba(16, 185, 129, 0.02)' : 'rgba(16, 185, 129, 0.05)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}>
                    {/* Row Number Indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '0.25rem',
                      left: '0.25rem',
                      background: 'rgba(59, 130, 246, 0.8)',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      padding: '0.2rem 0.4rem',
                      borderRadius: '4px',
                      zIndex: 10
                    }}>
                      Row {index + 1}
                    </div>
                  {csvUploadedItems.length > 0 && Object.keys(csvUploadedItems[0]).map((fieldName) => (
                    <div 
                      key={fieldName}
                      style={{
                        color: fieldName === 'medicine_name' ? '#1f2937' : '#6b7280',
                        fontWeight: fieldName === 'medicine_name' ? '600' : '400',
                        textAlign: 'center',
                        minWidth: '150px',
                        width: '150px',
                        wordBreak: 'break-word',
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={medicine[fieldName] || 'N/A'}
                    >
                      {medicine[fieldName] || 'N/A'}
                    </div>
                  ))}
                </div>
                    );
                  })}
            </div>
          </div>
        </div>
      )}

      {/* Table Footer */}
      <div style={{
        margin: '0 2rem 1rem 2rem',
        padding: '1.5rem',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(5, 150, 105, 0.03) 100%)',
        borderRadius: '16px',
        border: '1px solid rgba(59, 130, 246, 0.08)',
        borderTop: '2px solid rgba(16, 185, 129, 0.2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        fontSize: '1.1rem',
        fontWeight: '700',
        color: '#374151',
        boxShadow: '0 10px 40px rgba(59, 130, 246, 0.08), 0 4px 20px rgba(5, 150, 105, 0.05)',
        backdropFilter: 'blur(20px)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '60%',
          height: '140%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.02) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '40%',
          height: '120%',
          background: 'radial-gradient(circle, rgba(5, 150, 105, 0.02) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />
        
        <div style={{ 
          color: '#3b82f6',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 1
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Total Items: {rows.length}
      </div>
        <div style={{ 
          color: '#059669',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 1
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2V22M17 5H9.5C8.11929 5 7 6.11929 7 7.5S8.11929 10 9.5 10H14.5C15.8807 10 17 11.1193 17 12.5S15.8807 15 14.5 15H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Total Value: ₹0.00
        </div>
        
        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          className="submit-button"
          style={{
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '10px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            zIndex: 1,
            letterSpacing: '0.3px',
            textTransform: 'uppercase'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(34, 197, 94, 0.4)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Submit Data
        </button>
      </div>

          {/* Distributor Selection Section */}
          <DistributorDetails 
            selectedDistributor={selectedDistributor}
            onDistributorSelect={setSelectedDistributor}
          />

        </>
      )}

      {(activeTab as string) === 'checkout' && <CheckoutBilling showHeader={false} />}

      {(activeTab as string) === 'inventory' && <Inventory showHeader={false} />}

      {/* Edit Modal */}
      {showEditModal && editingPurchase && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '800px'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
    </div>
                Edit Purchase: {editingPurchase.pending_id}
              </h2>
              <button
                onClick={closeEditModal}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 3px 10px rgba(239, 68, 68, 0.3)',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.3)';
                }}
              >
                ×
              </button>
                      </div>
                      
            {/* Purchase Info */}
                      <div style={{
              background: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '8px',
              padding: '1rem',
               marginBottom: '1.5rem',
              border: '1px solid rgba(59, 130, 246, 0.1)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                fontSize: '0.9rem'
              }}>
                <div>
                  <span style={{ fontWeight: '600', color: '#374151' }}>Purchase ID:</span>
                  <div style={{ color: '#6b7280' }}>{editingPurchase.pending_id}</div>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#374151' }}>Status:</span>
                  <div style={{ color: '#6b7280' }}>{editingPurchase.status}</div>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#374151' }}>Created:</span>
                  <div style={{ color: '#6b7280' }}>
                    {editingPurchase.created_at ? new Date(editingPurchase.created_at).toLocaleString() : 'N/A'}
                  </div>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#374151' }}>Created By:</span>
                  <div style={{ color: '#6b7280' }}>User ID {editingPurchase.created_by}</div>
                </div>
              </div>
                      </div>

            {/* Purchase Details Form */}
            <div style={{ marginBottom: '1.5rem' }}>
               <h3 style={{
                fontSize: '1.2rem',
                 fontWeight: '600',
                 color: '#1f2937',
                marginBottom: '1rem',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '0.5rem'
               }}>
                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                 </svg>
                Purchase Details
               </h3>
                      
                      <div style={{
                background: 'rgba(16, 185, 129, 0.05)',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid rgba(16, 185, 129, 0.1)'
                 }}>
                   <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  {/* Distributor ID */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.8rem',
                        fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      Distributor ID
                    </label>
                    <input
                      type="number"
                      value={editFormData.distributor_id || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, distributor_id: parseInt(e.target.value) || null }))}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                     fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#10b981'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                      </div>
                      
                  {/* Distributor Name */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.8rem',
                        fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      Distributor Name
                    </label>
                    <input
                      type="text"
                      value={editFormData.distributor_name || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, distributor_name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#10b981'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                      </div>
                      
                  {/* Payment Mode */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.8rem',
                        fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      Payment Mode
                    </label>
                    <select
                      value={editFormData.payment_mode || 'credit'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, payment_mode: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        background: 'white'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#10b981'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    >
                      <option value="credit">Credit</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="online">Online</option>
                    </select>
                      </div>
                      
                  {/* Payment Details */}
                  <div>
                    <label style={{
                      display: 'block',
                        fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      Payment Details
                    </label>
                    <input
                      type="text"
                      value={editFormData.payment_details || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, payment_details: e.target.value }))}
                      placeholder="e.g., Net 30, Cash on delivery"
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#10b981'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                      </div>
                    </div>
                </div>
              </div>

            {/* Items Edit Form */}
            <div style={{ marginBottom: '1.5rem' }}>
               <h3 style={{
                fontSize: '1.2rem',
                 fontWeight: '600',
                 color: '#1f2937',
                marginBottom: '1rem',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '0.5rem'
               }}>
                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                       <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"/>
                 </svg>
                Edit Items ({editFormData.items?.length || 0})
               </h3>
               
                 <div style={{
                     display: 'grid',
                gap: '1rem',
                maxHeight: '400px',
                overflow: 'auto',
                padding: '0.5rem'
              }}>
                {editFormData.items?.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} style={{
                    background: 'rgba(59, 130, 246, 0.05)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid rgba(59, 130, 246, 0.1)',
                    transition: 'all 0.3s ease'
                 }}>
                   <div style={{
                     display: 'flex',
                      justifyContent: 'space-between',
                     alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <h4 style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1f2937',
                        margin: 0
                      }}>
                        Item {itemIndex + 1}: {item.medicine_name || 'Unknown Medicine'}
                      </h4>
                   </div>
                    
                   <div style={{
                     display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem'
                    }}>
                      {/* Medicine Name */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Medicine Name
                        </label>
                        <input
                          type="text"
                          value={item.medicine_name || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'medicine_name', e.target.value)}
                          style={{
                            width: '100%',
                       padding: '0.5rem',
                            border: '1px solid #d1d5db',
                       borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>

                      {/* Medicine Company */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Medicine Company
                        </label>
                        <input
                          type="text"
                          value={item.medicine_company || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'medicine_company', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                       </div>

                      {/* Batch Number */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Batch Number
                        </label>
                        <input
                          type="text"
                          value={item.batch_no || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'batch_no', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                     </div>

                      {/* Expiry Date */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Expiry Date
                        </label>
                        <input
                          type="date"
                          value={item.expiry_date || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'expiry_date', e.target.value)}
                          style={{
                            width: '100%',
                       padding: '0.5rem',
                            border: '1px solid #d1d5db',
                       borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>

                      {/* Quantity */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Quantity
                        </label>
                        <input
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'quantity', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                       </div>

                      {/* Cost Price Per Unit */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Cost Price Per Unit (₹)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.cost_price_per_unit || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'cost_price_per_unit', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                     </div>

                      {/* MRP */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          MRP (₹)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.mrp || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'mrp', e.target.value)}
                          style={{
                            width: '100%',
                       padding: '0.5rem',
                            border: '1px solid #d1d5db',
                       borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>

                      {/* Tax Amount */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Tax Amount (₹)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.tax_amount || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'tax_amount', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                       </div>

                      {/* Storage Location */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Storage Location
                        </label>
                        <input
                          type="text"
                          value={item.storage_location || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'storage_location', e.target.value)}
                          placeholder="e.g., A1, B2, C3"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                     </div>

                      {/* Quantity Type */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Quantity Type
                        </label>
                        <select
                          value={item.quantity_type || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'quantity_type', e.target.value)}
                          style={{
                            width: '100%',
                       padding: '0.5rem',
                            border: '1px solid #d1d5db',
                       borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease',
                            background: 'white'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        >
                          <option value="strip">Strip</option>
                          <option value="bottle">Bottle</option>
                          <option value="tablet">Tablet</option>
                          <option value="capsule">Capsule</option>
                          <option value="syrup">Syrup</option>
                        </select>
                      </div>

                      {/* Tablets per Strip */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Tablets per Strip
                        </label>
                        <input
                          type="number"
                          value={item.tablets_per_strip || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'tablets_per_strip', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>

                      {/* Total Tablets */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          Total Tablets
                        </label>
                        <input
                          type="number"
                          value={item.total_tablets || ''}
                          onChange={(e) => handleItemFieldChange(itemIndex, 'total_tablets', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={closeEditModal}
                disabled={isUpdatingPurchase}
                style={{
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: isUpdatingPurchase ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isUpdatingPurchase ? 0.7 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={updatePendingPurchase}
                disabled={isUpdatingPurchase}
                style={{
                  background: isUpdatingPurchase 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: isUpdatingPurchase ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isUpdatingPurchase) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isUpdatingPurchase) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
              >
                {isUpdatingPurchase ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                    </svg>
                    Update Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CSV Mapping Modal */}
      {showCsvMappingModal && csvUploadedItems.length > 0 && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(2,6,23,0.55)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: isMobile ? '0.5rem' : '0',
          animation: 'fadeIn 180ms ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.98) 100%)',
            borderRadius: isMobile ? '12px' : '0px',
            width: isMobile ? 'calc(100vw - 1rem)' : '100vw',
            height: isMobile ? 'calc(100vh - 1rem)' : '100vh',
            maxWidth: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid rgba(226,232,240,0.7)',
            boxShadow: isMobile ? '0 20px 40px rgba(2, 6, 23, 0.25)' : '0 30px 80px rgba(2, 6, 23, 0.35)',
            transform: 'translateY(8px)',
            animation: 'slideInUp 260ms cubic-bezier(0.22,1,0.36,1) forwards'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: isMobile ? '1rem' : '1.25rem 1.5rem',
              borderBottom: '1px solid rgba(226,232,240,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'white',
              color: '#1f2937',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: isMobile ? '0.75rem' : '1rem'
            }}>
              <div style={{ 
                fontWeight: 700, 
                letterSpacing: '-0.025em', 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? '0.5rem' : '0.75rem',
                fontSize: isMobile ? '1rem' : '1.125rem',
                color: '#111827'
              }}>
                <div style={{
                  width: isMobile ? '32px' : '36px', 
                  height: isMobile ? '32px' : '36px', 
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                }}>
                  <svg width={isMobile ? "16" : "18"} height={isMobile ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M3 7h18M3 12h18M3 17h18"/>
                  </svg>
                </div>
                <span style={{ display: isMobile ? 'none' : 'inline' }}>CSV Field Mapping</span>
                <span style={{ display: isMobile ? 'inline' : 'none' }}>CSV Mapping</span>
              </div>
              
              {/* Switch Buttons in Header */}
              <div style={{ 
                position: 'relative',
                display: 'flex',
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '4px',
                border: '1px solid #e2e8f0',
                minWidth: isMobile ? '160px' : '180px'
              }}>
                {/* Sliding background */}
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  left: csvPreviewMode === 'columns' ? '4px' : '50%',
                  width: 'calc(50% - 4px)',
                  height: 'calc(100% - 8px)',
                  background: 'white',
                  borderRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  zIndex: 1
                }} />
                
                <button
                  onClick={() => setCsvPreviewMode('columns')}
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    background: 'transparent',
                    color: csvPreviewMode === 'columns' ? '#1f2937' : '#6b7280',
                    border: 'none',
                    padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: 1,
                    textAlign: 'center'
                  }}
                >Map Fields</button>
                <button
                  onClick={() => setCsvPreviewMode('preview')}
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    background: 'transparent',
                    color: csvPreviewMode === 'preview' ? '#1f2937' : '#6b7280',
                    border: 'none',
                    padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: 1,
                    textAlign: 'center'
                  }}
                >Data Preview</button>
              </div>
              
              {/* Clear All Button in Header */}
              {(Object.keys(fieldMapping).length > 0 || Object.keys(tempFieldMapping).length > 0) && (
                <button
                  onClick={clearAllFieldMappings}
                  style={{
                    background: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                    padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)'
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.background = '#fee2e2';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.15)';
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.background = '#fef2f2';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(220, 38, 38, 0.1)';
                  }}
                  title="Clear all field mappings"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  </svg>
                  <span style={{ display: isMobile ? 'none' : 'inline' }}>Clear All</span>
                </button>
              )}
              
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button
                  onClick={() => setShowCsvMappingModal(false)}
                  style={{
                    background: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    minWidth: isMobile ? '80px' : '100px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6L18 18"/>
                  </svg>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setFieldMapping(prev => ({ ...prev, ...tempFieldMapping }));
                    Object.entries(tempFieldMapping).forEach(([targetField, csvField]) => { if (csvField) { autoFillColumn(targetField, csvField); } });
                    setShowCsvMappingModal(false);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    border: 'none',
                    padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                    minWidth: isMobile ? '80px' : '100px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.35)';
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"/>
                  </svg>
                  Done
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: csvPreviewMode === 'preview' ? '1fr' : '1fr 1fr',
              gridTemplateRows: csvPreviewMode === 'preview' ? '1fr auto' : '1fr',
              flex: 1,
              minHeight: 0,
              background: 'transparent',
              gap: csvPreviewMode === 'preview' ? '1rem' : isMobile ? '0.25rem' : '0',
              height: '100%',
              paddingTop: isMobile ? '1rem' : '1.5rem',
              overflow: csvPreviewMode === 'preview' ? 'visible' : 'visible'
            }}>
              {/* Left: CSV Fields and Sample Data (draggable) */}
              <div style={{
                borderRight: csvPreviewMode === 'preview' ? 'none' : isMobile ? '1px solid rgba(226,232,240,0.4)' : '1px solid rgba(226,232,240,0.8)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                background: 'linear-gradient(180deg, rgba(16,185,129,0.03) 0%, rgba(5,150,105,0.02) 100%)',
                overflow: 'hidden',
                height: '100%'
              }}>
                <div style={{ 
                  padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', 
                  color: '#111827', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  gap: '0.5rem',
                  fontSize: isMobile ? '0.8rem' : '1rem',
                  flexShrink: 0
                }}>
                  <div style={{ fontWeight: 900, letterSpacing: '0.2px' }}>CSV</div>
                </div>
                {csvPreviewMode === 'columns' ? (
                  <div style={{ 
                    overflow: 'auto', 
                    padding: isMobile ? '0 0.5rem 0.5rem 0.5rem' : '0 1rem 1rem 1rem',
                    flex: 1,
                    minHeight: 0
                  }}>
                    {csvUploadedItems.length > 0 && Object.keys(csvUploadedItems[0]).map((fieldName) => (
                      <div
                        key={fieldName}
                        draggable
                        onDragStart={(e) => handleDragStart(e as any, fieldName)}
                        onDragEnd={handleDragEnd}
                        onTouchStart={(e) => handleTouchStart(e, fieldName)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{
                          cursor: 'grab',
                          padding: isMobile ? '0.5rem 0.6rem' : '0.8rem 1rem',
                          marginBottom: isMobile ? '0.4rem' : '0.75rem',
                          border: '2px solid rgba(59, 130, 246, 0.15)',
                          borderRadius: isMobile ? '6px' : '12px',
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(37, 99, 235, 0.05) 100%)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.08)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden',
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: isMobile ? '0.3rem' : '0',
                          transform: isTouchDragging && draggedField === fieldName ? 'scale(1.05)' : 'scale(1)',
                          zIndex: isTouchDragging && draggedField === fieldName ? 1000 : 'auto',
                          touchAction: isMobile ? 'none' : 'auto'
                        }}
                        onMouseEnter={(e) => { 
                          e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; 
                          e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => { 
                          e.currentTarget.style.transform = 'translateY(0) scale(1)'; 
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.15)';
                        }}
                      >
                        {/* Subtle pattern overlay */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '40px',
                          height: '40px',
                          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                          borderRadius: '50%',
                          transform: 'translate(20px, -20px)'
                        }} />
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.4rem' : '0.75rem', position: 'relative', zIndex: 1 }}>
                          {/* Field icon */}
                          <div style={{
                            width: isMobile ? '20px' : '32px',
                            height: isMobile ? '20px' : '32px',
                            borderRadius: isMobile ? '4px' : '8px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: isMobile ? '0.6rem' : '0.8rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                            flexShrink: 0
                          }}>
                            📊
                          </div>
                          
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ 
                              fontWeight: 700, 
                              color: '#1e40af',
                              fontSize: isMobile ? '0.7rem' : '0.9rem',
                              marginBottom: '0.2rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '0.6rem' : '0.75rem',
                              color: '#64748b',
                              fontWeight: 500
                            }}>
                              CSV Field
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: isMobile ? '0.3rem' : '0.5rem',
                          color: '#3b82f6',
                          fontSize: isMobile ? '0.7rem' : '0.8rem',
                          fontWeight: 600,
                          position: 'relative',
                          zIndex: 1,
                          flexShrink: 0
                        }}>
                          <span style={{ display: isMobile ? 'none' : 'inline' }}>Drag to map</span>
                          <span style={{ display: isMobile ? 'inline' : 'none' }}>{isTouchDragging && draggedField === fieldName ? 'Release to drop' : 'Touch & drag'}</span>
                          <div style={{
                            width: isMobile ? '16px' : '20px',
                            height: isMobile ? '16px' : '20px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 6px rgba(59, 130, 246, 0.4)',
                            animation: isTouchDragging && draggedField === fieldName ? 'pulse 1s infinite' : 'pulse 2s infinite'
                          }}>↔</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    padding: '0 1rem 0 1rem', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    minHeight: 0
                  }}>
                    {/* Scrollable Table Container */}
                    <div 
                      className="csv-preview-scroll-container"
                      style={{
                        background: 'white',
                        border: '2px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        overflow: 'auto',
                        maxHeight: isMobile ? '400px' : 'calc(35vh)',
                        height: isMobile ? '400px' : 'calc(35vh)',
                        WebkitOverflowScrolling: 'touch' as any,
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        width: '100%',
                        position: 'relative',
                        padding: '0',
                        margin: '0'
                      }}>
                        {/* Table Header */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: Object.keys(csvUploadedItems[0] || {}).map(() => '240px').join(' '),
                          gap: '12px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          color: 'white',
                          padding: '0.75rem 0.8rem',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                          borderRadius: '8px 8px 0 0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          minWidth: `${(Object.keys(csvUploadedItems[0] || {}).length || 1) * 240 + (Object.keys(csvUploadedItems[0] || {}).length - 1) * 12 + 32}px`
                        }}>
                          {Object.keys(csvUploadedItems[0] || {}).map((field, index) => (
                            <div
                              key={field}
                              draggable
                              onDragStart={(e) => handleDragStart(e as any, field)}
                              onDragEnd={handleDragEnd}
                              style={{ 
                                minWidth: '240px', 
                                cursor: 'grab', 
                                userSelect: 'none' as any,
                                padding: '0.5rem',
                                borderRadius: '8px',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                animation: `slideInDown 0.3s ease-out ${index * 0.05}s both`
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                              title={`Drag "${field.replace(/_/g, ' ')}" to a medication field`}
                            >
                              <span>
                                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Table Body Rows - All rows with scrollable container */}
                        {csvUploadedItems.map((row, rIdx) => {
                          console.log(`Rendering CSV preview row ${rIdx + 1}:`, row);
                          return (
                            <div 
                              key={rIdx} 
                              style={{ 
                                display: 'grid',
                                gridTemplateColumns: Object.keys(csvUploadedItems[0] || {}).map(() => '240px').join(' '),
                                gap: '12px',
                                padding: isMobile ? '0.8rem 0.6rem' : '0.6rem 0.8rem', 
                                background: rIdx % 2 === 0 ? '#ffffff' : '#f8fafc',
                                borderBottom: rIdx < csvUploadedItems.length - 1 ? '1px solid #f1f5f9' : 'none',
                                transition: 'all 0.2s ease',
                                animation: `slideInUp 0.4s ease-out ${rIdx * 0.02}s both`,
                                position: 'relative',
                                minWidth: `${(Object.keys(csvUploadedItems[0] || {}).length || 1) * 240 + (Object.keys(csvUploadedItems[0] || {}).length - 1) * 12 + 32}px`,
                                minHeight: isMobile ? '60px' : '45px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.transform = 'translateX(4px)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = rIdx % 2 === 0 ? '#ffffff' : '#f8fafc';
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              {Object.keys(csvUploadedItems[0] || {}).map((field, fieldIdx) => (
                                <div 
                                  key={field} 
                                  style={{ 
                                    minWidth: '240px', 
                                    color: '#374151', 
                                    fontSize: '0.875rem', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    padding: isMobile ? '0.6rem' : '0.4rem',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease',
                                    fontWeight: fieldIdx === 0 ? 600 : 400,
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: isMobile ? '40px' : '30px'
                                  }} 
                                  title={String(row[field] ?? '')}
                                >
                                  <span>
                                    {String(row[field] ?? '')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                        
                        {/* Extra spacing to ensure scrolling */}
                        <div style={{ height: '20px', background: 'transparent' }}></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Medication Fields (droppable targets) - below CSV in preview mode, side in columns mode */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                gridColumn: csvPreviewMode === 'preview' ? '1 / -1' : 'auto',
                marginTop: csvPreviewMode === 'preview' ? '0' : '0',
                height: 'auto',
                maxHeight: csvPreviewMode === 'preview' ? '300px' : '600px',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                scrollbarColor: '#3b82f6 #f1f5f9'
              }}>
                <div style={{ 
                  padding: isMobile ? '0 0.75rem 0.4rem 0.75rem' : '0 1.1rem 0.6rem 1.1rem', 
                  fontWeight: 900, 
                  color: '#111827', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  borderBottom: csvPreviewMode === 'preview' ? 'none' : '1px solid rgba(226,232,240,0.8)', 
                  borderTop: csvPreviewMode === 'preview' ? '1px solid rgba(226,232,240,0.8)' : 'none', 
                  fontSize: isMobile ? '0.8rem' : '1rem',
                  flexShrink: 0
                }}>
                  <span>Medication Fields</span>
                </div>
                <div style={{ 
                  padding: isMobile ? '0 0.5rem 0.5rem 0.5rem' : '0 1rem 1rem 1rem', 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(auto-fit, minmax(280px, 1fr))', 
                  gap: isMobile ? '0.4rem' : '0.75rem',
                  flex: 1,
                  minHeight: 0
                }}>
                  {[
                    { key: 'medicineName', label: 'Medicine Name' },
                    { key: 'composition', label: 'Composition' },
                    { key: 'brandName', label: 'Brand Name' },
                    { key: 'batchNumber', label: 'Batch Number' },
                    { key: 'quantity', label: 'Quantity' },
                    { key: 'quantityType', label: 'Quantity Type' },
                    { key: 'tabletsPerStrip', label: 'Tablets Per Strip' },
                    { key: 'costPrice', label: 'Cost Price' },
                    { key: 'mrp', label: 'MRP' },
                    { key: 'gstRate', label: 'GST Rate' },
                    { key: 'expiryDate', label: 'Expiry Date' },
                    { key: 'purchaseDate', label: 'Purchase Date' }
                  ].map(({ key, label }) => (
                    <div
                      key={key}
                      data-drop-target={key}
                      onDragOver={handleFieldDragOver}
                      onDrop={(e) => {
                        e.preventDefault();
                        const draggedFieldName = (e as any).dataTransfer.getData('text/plain');
                        if (draggedFieldName) {
                          setTempFieldMapping(prev => ({ ...prev, [key]: draggedFieldName }));
                        }
                      }}
                      style={{
                        padding: isMobile ? '0.5rem 0.6rem' : '0.8rem 1rem',
                        borderRadius: isMobile ? '6px' : '12px',
                        border: tempFieldMapping[key] ? '2px solid rgba(16,185,129,0.4)' : '2px solid rgba(226,232,240,0.6)',
                        background: tempFieldMapping[key] 
                          ? 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(5,150,105,0.12) 100%)' 
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: isMobile ? '0.3rem' : '0.5rem',
                        boxShadow: tempFieldMapping[key] 
                          ? '0 4px 12px rgba(16,185,129,0.15)' 
                          : '0 2px 8px rgba(2,6,23,0.05)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        minHeight: isMobile ? '60px' : '80px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => { 
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; 
                        e.currentTarget.style.boxShadow = tempFieldMapping[key] 
                          ? '0 8px 20px rgba(16,185,129,0.2)' 
                          : '0 6px 16px rgba(2,6,23,0.1)';
                      }}
                      onMouseLeave={(e) => { 
                        e.currentTarget.style.transform = 'translateY(0) scale(1)'; 
                        e.currentTarget.style.boxShadow = tempFieldMapping[key] 
                          ? '0 4px 12px rgba(16,185,129,0.15)' 
                          : '0 2px 8px rgba(2,6,23,0.05)';
                      }}
                    >
                      {/* Subtle pattern overlay for medication fields */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '30px',
                        height: '30px',
                        background: tempFieldMapping[key] 
                          ? 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)'
                          : 'radial-gradient(circle, rgba(148,163,184,0.05) 0%, transparent 70%)',
                        borderRadius: '50%',
                        transform: 'translate(15px, -15px)'
                      }} />
                      
                      <div style={{ fontWeight: 800, color: '#0f172a', fontSize: isMobile ? '0.7rem' : '0.9rem', textAlign: 'center', position: 'relative', zIndex: 1 }}>{label}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', width: '100%', position: 'relative', zIndex: 1 }}>
                        {tempFieldMapping[key] && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            background: 'rgba(16,185,129,0.12)',
                            color: '#059669',
                            border: '1px solid rgba(16,185,129,0.25)',
                              padding: '0.25rem 0.6rem',
                            borderRadius: '999px',
                              fontSize: '0.8rem',
                            fontWeight: 700
                          }}>
                            {tempFieldMapping[key].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTempFieldMapping(prev => {
                                  const newMapping = { ...prev };
                                  delete newMapping[key];
                                  return newMapping;
                                });
                              }}
                              style={{
                                background: 'rgba(239, 68, 68, 0.8)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title="Clear field mapping"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <span style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'center' }}>
                          {tempFieldMapping[key] ? 'Mapped' : 'Drop CSV field here'}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Existing dynamic fields (if any) as droppable targets */}
                  {dynamicFields.map((fieldKey) => (
                    <div
                      key={fieldKey}
                      onDragOver={handleFieldDragOver}
                      onDrop={(e) => {
                        e.preventDefault();
                        const draggedFieldName = (e as any).dataTransfer.getData('text/plain');
                        if (draggedFieldName) {
                          setTempFieldMapping(prev => ({ ...prev, [fieldKey]: draggedFieldName }));
                        }
                      }}
                      style={{
                        padding: '0.7rem 0.85rem',
                        borderRadius: '10px',
                        border: tempFieldMapping[fieldKey] ? '2px dashed rgba(16,185,129,0.5)' : '2px dashed #e5e7eb',
                        background: tempFieldMapping[fieldKey] ? 'linear-gradient(180deg, rgba(16,185,129,0.06) 0%, rgba(5,150,105,0.06) 100%)' : 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '0.75rem',
                        boxShadow: '0 2px 8px rgba(2,6,23,0.05)'
                      }}
                    >
                      <div style={{ fontWeight: 600, color: '#111827' }}>{(fieldMapping[fieldKey] ? fieldMapping[fieldKey] : 'Add Field').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {tempFieldMapping[fieldKey] && (
                          <span style={{
                            background: 'rgba(16,185,129,0.12)',
                            color: '#059669',
                            border: '1px solid rgba(16,185,129,0.25)',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '999px',
                            fontSize: '0.75rem',
                            fontWeight: 700
                          }}>
                            {tempFieldMapping[fieldKey].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        )}
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {tempFieldMapping[fieldKey] ? 'Mapped' : 'Drop CSV field here'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default MedicationAddition;